import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import {
  DndContext, DragOverlay, closestCenter,
  KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { uid, formatDate, stripHtml, stripHtmlKeepSpaces, hasVisibleContent } from '../utils';
import { DEFAULT_COLORS, TIMELINE_CATEGORIES, TIMELINE_CATEGORY_GROUPS } from '../constants';
import { Storage } from '../storage';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import EventForm       from '../components/EventForm';
import CSVImportModal   from '../components/CSVImportModal';
import GEDCOMImportModal from '../components/GEDCOMImportModal';
import ColorCustomizer  from '../components/ColorCustomizer';
import TimelineCanvas from '../components/TimelineCanvas';
import { FilmstripSettings } from '../components/FilmstripTimeline';
import NotificationBell from '../components/NotificationBell';
import MobileMenu from '../components/MobileMenu';

// ─── SORTABLE EVENT CARD ─────────────────────────────────────────────────────
//
//  Used inside DndContext/SortableContext in the Events tab.
//  The drag handle (grip dots) is the only trigger for dragging — clicking the
//  rest of the card still fires the edit/duplicate/delete buttons normally.
//  readOnly: hides drag handle and action buttons for viewer-role collaborators.

const SortableEventCard = ({ ev, position, manualOrder, onEdit, onDuplicate, onDelete, onFocused, readOnly, showPhotoBadge, showNpBadges, showMapBadge }) => {
  const [hovered, setHovered] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: ev.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity:   isDragging ? 0.35 : 1,
    background:  'var(--surface)',
    border:      `1px solid ${isDragging ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 'var(--radius)',
    padding:     '12px 14px',
    marginBottom: 10,
    display:     'flex',
    alignItems:  'flex-start',
    gap:         8,
  };

  return (
    <li
      ref={setNodeRef}
      tabIndex={0}
      data-event-card="true"
      onFocus={() => onFocused?.(ev.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={style}
    >

      {/* Drag handle — hidden for read-only viewers */}
      {!readOnly && (
        <div
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder ${ev.title}`}
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
            color: hovered ? 'var(--text2)' : 'transparent',
            flexShrink: 0, paddingTop: 2,
            transition: 'color 0.15s',
            touchAction: 'none',
          }}
        >
          <Icon name="grip" size={14} fill="currentColor" />
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}>
          {manualOrder && (
            <span style={{
              flexShrink: 0, fontSize: 10, fontWeight: 700,
              background: 'var(--accent)22', color: 'var(--accent)',
              border: '1px solid var(--accent)40',
              borderRadius: 4, padding: '1px 5px', lineHeight: '16px',
            }}>
              {position}
            </span>
          )}
          <p style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
            {ev.title}
          </p>
        </div>
        <p style={{ fontSize: 11, color: 'var(--text3)' }}>
          {formatDate(ev.startDate, ev.startEra !== 'NONE')}{ev.endDate && ev.endDate !== ev.startDate ? ` → ${formatDate(ev.endDate, ev.endEra !== 'NONE')}` : ''}
        </p>
        {ev.chapter && (
          <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 600, background: 'var(--accent)15', color: 'var(--accent)', border: '1px solid var(--accent)30', borderRadius: 4, padding: '1px 5px', marginTop: 3 }}>
            {ev.chapter}
          </span>
        )}
        {showPhotoBadge && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600, borderRadius: 4, padding: '1px 5px', marginTop: 3,
            background: ev.photo ? '#15803d22' : '#92400e22',
            color:      ev.photo ? '#15803d'   : '#d97706',
            border:     `1px solid ${ev.photo ? '#15803d40' : '#92400e40'}`,
          }}>
            {ev.photo ? '✓ Photo' : '📷 No photo'}
          </span>
        )}
        {showNpBadges && ev.featured && (
          <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 600, borderRadius: 4, padding: '1px 5px', marginTop: 3, marginLeft: 2,
            background: '#eab30822', color: '#b45309', border: '1px solid #eab30840',
          }}>
            ⭐ Featured
          </span>
        )}
        {showNpBadges && ev.breakingNews && (
          <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 600, borderRadius: 4, padding: '1px 5px', marginTop: 3, marginLeft: 2,
            background: '#ef444422', color: '#dc2626', border: '1px solid #ef444440',
          }}>
            🔴 Breaking
          </span>
        )}
        {showMapBadge && !(isFinite(ev.location?.lat) && ev.location?.lat >= -90 && ev.location?.lat <= 90 && isFinite(ev.location?.lng) && ev.location?.lng >= -180 && ev.location?.lng <= 180) && (
          <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 600, borderRadius: 4, padding: '1px 5px', marginTop: 3, marginLeft: 2,
            background: '#64748b22', color: '#64748b', border: '1px solid #64748b40',
          }}>
            📍 No location
          </span>
        )}
      </div>

      {/* Action buttons — hidden for read-only viewers */}
      {!readOnly && (
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button className="icon-btn" onClick={onDuplicate} aria-label={`Duplicate ${ev.title}`}>
            <Icon name="duplicate" size={14} />
          </button>
          <button className="icon-btn" onClick={onEdit} aria-label={`Edit ${ev.title}`}>
            <Icon name="edit" size={14} />
          </button>
          <button className="icon-btn danger" onClick={onDelete} aria-label={`Delete ${ev.title}`}>
            <Icon name="trash" size={14} />
          </button>
        </div>
      )}
    </li>
  );
};

// ─── SORTABLE CHAPTER ROW ─────────────────────────────────────────────────────
//
//  Used inside the Chapters tab to reorder, rename, delete, and bulk-assign
//  events to chapters using the same dnd-kit setup as event reordering.

// Curated emoji set for chapter icons (60 emojis grouped loosely by theme)
const CHAPTER_EMOJIS = [
  '📅','⏱','🕐','📆','🗓','⌛','🔮','🌟','💡','🔍',
  '📌','📍','🏁','🎯','🎬','🚀','⚡','🔑','🗝','💎',
  '🌱','🌿','🌊','🔥','🌙','☀️','⭐','🌈','🍃','🌺',
  '💼','📊','📈','🏆','🎖','🗺','🏛','🔬','🧪','🛠',
  '👥','🤝','💬','🎓','📚','✍','🎨','🎵','🎭','🏠',
  '❤️','💪','🧠','🎉','🔔','⚙️','🛡','📡','🌐','✅',
];

const SortableChapterRow = ({
  chapter,   // { name, color?, summary?, icon? }
  eventCount, allEvents,
  isRenaming, renameValue, onRenameStart, onRenameChange, onRenameCommit, onRenameCancel,
  onDelete, isAssigning, onToggleAssign, onEventAssign,
  onMetaUpdate,  // (patch) => void — updates color/summary/icon
}) => {
  const name = chapter.name;
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(!!chapter.summary);
  const colorPickerRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Close pickers on outside click
  useEffect(() => {
    const handler = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) setShowColorPicker(false);
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) setShowEmojiPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: name });

  const style = {
    transform:    CSS.Transform.toString(transform),
    transition,
    opacity:      isDragging ? 0.4 : 1,
    background:   'var(--surface)',
    border:       `1px solid ${isDragging ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 'var(--radius)',
    marginBottom: 8,
  };

  return (
    <li ref={setNodeRef} style={style}>
      {/* ── Main row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px' }}>
        <div
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder chapter ${name}`}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', color: 'var(--text3)', flexShrink: 0, touchAction: 'none' }}
        >
          <Icon name="grip" size={13} fill="currentColor" />
        </div>

        {/* Icon picker button */}
        <div style={{ position: 'relative', flexShrink: 0 }} ref={emojiPickerRef}>
          <button
            className="icon-btn"
            onClick={() => { setShowEmojiPicker(p => !p); setShowColorPicker(false); }}
            title={chapter.icon ? `Icon: ${chapter.icon}` : 'Add chapter icon'}
            aria-label="Set chapter icon"
            style={{ fontSize: 14, width: 28, height: 28 }}
          >
            {chapter.icon || '😶'}
          </button>
          {showEmojiPicker && (
            <div className="sl-emoji-picker" style={{ top: 32, left: 0 }}>
              <div className="sl-emoji-grid">
                {CHAPTER_EMOJIS.map(em => (
                  <button
                    key={em}
                    className="sl-emoji-btn"
                    onClick={() => { onMetaUpdate({ icon: em }); setShowEmojiPicker(false); }}
                    title={em}
                  >
                    {em}
                  </button>
                ))}
              </div>
              {chapter.icon && (
                <button
                  className="sl-emoji-clear"
                  onClick={() => { onMetaUpdate({ icon: null }); setShowEmojiPicker(false); }}
                >
                  Remove Icon
                </button>
              )}
            </div>
          )}
        </div>

        {/* Chapter name — renaming or display */}
        {isRenaming ? (
          <input
            autoFocus
            value={renameValue}
            onChange={e => onRenameChange(e.target.value)}
            onBlur={() => onRenameCommit(renameValue)}
            onKeyDown={e => {
              if (e.key === 'Enter') onRenameCommit(renameValue);
              if (e.key === 'Escape') onRenameCancel();
            }}
            style={{ flex: 1, fontSize: 13, padding: '4px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--accent)', background: 'var(--bg)', color: 'var(--text)' }}
          />
        ) : (
          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </span>
        )}

        <span style={{ fontSize: 11, background: 'var(--bg3)', borderRadius: 10, padding: '2px 7px', color: 'var(--text3)', flexShrink: 0 }}>
          {eventCount}
        </span>

        {/* Color swatch picker */}
        <div style={{ position: 'relative', flexShrink: 0 }} ref={colorPickerRef}>
          <button
            className="icon-btn"
            onClick={() => { setShowColorPicker(p => !p); setShowEmojiPicker(false); }}
            title={chapter.color ? `Color: ${chapter.color}` : 'Set chapter color'}
            aria-label="Set chapter color"
            style={{
              width: 20, height: 20, borderRadius: 4, padding: 0,
              background: chapter.color || 'var(--bg3)',
              border: `2px solid ${chapter.color ? 'transparent' : 'var(--border2)'}`,
            }}
          />
          {showColorPicker && (
            <div style={{
              position: 'absolute', zIndex: 300, top: 26, right: 0,
              background: 'var(--bg2)', border: '1px solid var(--border2)',
              borderRadius: 'var(--radius-lg)', padding: 12, boxShadow: '0 8px 28px rgba(0,0,0,0.4)',
              display: 'flex', flexDirection: 'column', gap: 8, width: 160,
            }}>
              <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 0 }}>Chapter Color</label>
              <input
                type="color"
                value={chapter.color || '#c9a96e'}
                onChange={e => onMetaUpdate({ color: e.target.value })}
                style={{ width: '100%', height: 36, padding: 2, cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'none' }}
              />
              {chapter.color && (
                <button
                  style={{ fontSize: 11, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                  onClick={() => { onMetaUpdate({ color: null }); setShowColorPicker(false); }}
                >
                  Clear color
                </button>
              )}
            </div>
          )}
        </div>

        <button
          className="icon-btn"
          onClick={onToggleAssign}
          aria-label={`Bulk assign events to ${name}`}
          title="Bulk assign events"
          aria-expanded={isAssigning}
        >
          <Icon name="users" size={13} />
        </button>
        {!isRenaming && (
          <button
            className="icon-btn"
            onClick={() => onRenameStart(name)}
            aria-label={`Rename chapter ${name}`}
          >
            <Icon name="pencil" size={13} />
          </button>
        )}
        <button
          className="icon-btn danger"
          onClick={() => onDelete(name)}
          aria-label={`Delete chapter ${name}`}
          title="Remove chapter (events are kept)"
        >
          <Icon name="trash" size={13} />
        </button>
      </div>

      {/* ── Summary field ── */}
      <div style={{ padding: '0 12px 10px' }}>
        {summaryExpanded ? (
          <>
            <textarea
              className="sl-summary-area"
              value={chapter.summary || ''}
              onChange={e => onMetaUpdate({ summary: e.target.value.slice(0, 500) })}
              placeholder="Optional chapter summary (shown as an intro card before events)…"
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="sl-summary-counter">{(chapter.summary || '').length} / 500</span>
              {!chapter.summary && (
                <button
                  style={{ fontSize: 11, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  onClick={() => setSummaryExpanded(false)}
                >
                  Collapse
                </button>
              )}
            </div>
          </>
        ) : (
          <button
            style={{ fontSize: 11, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            onClick={() => setSummaryExpanded(true)}
          >
            {chapter.summary ? 'Edit summary…' : '+ Add summary'}
          </button>
        )}
      </div>

      {/* ── Bulk assign panel ── */}
      {isAssigning && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 12px', maxHeight: 200, overflowY: 'auto' }}>
          <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Assign events
          </p>
          {allEvents.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>
              No events yet — add events in the Events tab first.
            </p>
          ) : (
            allEvents.map(ev => (
              <label key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={ev.chapter === name}
                  onChange={e => onEventAssign(ev.id, e.target.checked ? name : null)}
                  style={{ accentColor: 'var(--accent)', flexShrink: 0 }}
                />
                <span style={{ fontSize: 12, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ev.title}
                </span>
              </label>
            ))
          )}
        </div>
      )}
    </li>
  );
};

// ─── SORTABLE SWIMLANE ROW ────────────────────────────────────────────────────

const SortableSwimlaneRow = ({
  name, color, eventCount, allEvents,
  isRenaming, renameValue, onRenameStart, onRenameChange, onRenameCommit, onRenameCancel,
  onDelete, onColorChange, isAssigning, onToggleAssign, onEventAssign,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: name });

  const style = {
    transform:    CSS.Transform.toString(transform),
    transition,
    opacity:      isDragging ? 0.4 : 1,
    background:   'var(--surface)',
    border:       `1px solid ${isDragging ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 'var(--radius)',
    marginBottom: 8,
  };

  return (
    <li ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
        <div
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder swimlane ${name}`}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', color: 'var(--text3)', flexShrink: 0, touchAction: 'none' }}
        >
          <Icon name="grip" size={13} fill="currentColor" />
        </div>

        {/* Color swatch */}
        <label
          title="Change lane color"
          style={{ position: 'relative', flexShrink: 0, width: 18, height: 18, borderRadius: 4, background: color || 'var(--accent)', cursor: 'pointer', border: '1px solid var(--border2)' }}
        >
          <input
            type="color"
            value={color || '#FFB612'}
            onChange={e => onColorChange(e.target.value)}
            style={{ opacity: 0, position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'pointer', border: 'none', padding: 0 }}
          />
        </label>

        {isRenaming ? (
          <input
            autoFocus
            value={renameValue}
            onChange={e => onRenameChange(e.target.value)}
            onBlur={() => onRenameCommit(renameValue)}
            onKeyDown={e => {
              if (e.key === 'Enter') onRenameCommit(renameValue);
              if (e.key === 'Escape') onRenameCancel();
            }}
            style={{ flex: 1, fontSize: 13, padding: '4px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--accent)', background: 'var(--bg)', color: 'var(--text)' }}
          />
        ) : (
          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </span>
        )}

        <span style={{ fontSize: 11, background: 'var(--bg3)', borderRadius: 10, padding: '2px 7px', color: 'var(--text3)', flexShrink: 0 }}>
          {eventCount}
        </span>
        <button
          className="icon-btn"
          onClick={onToggleAssign}
          aria-label={`Bulk assign events to ${name}`}
          title="Bulk assign events"
          aria-expanded={isAssigning}
        >
          <Icon name="users" size={13} />
        </button>
        {!isRenaming && (
          <button
            className="icon-btn"
            onClick={() => onRenameStart(name)}
            aria-label={`Rename swimlane ${name}`}
          >
            <Icon name="pencil" size={13} />
          </button>
        )}
        <button
          className="icon-btn danger"
          onClick={() => onDelete(name)}
          aria-label={`Delete swimlane ${name}`}
          title="Remove swimlane (events are kept)"
        >
          <Icon name="trash" size={13} />
        </button>
      </div>

      {isAssigning && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 12px', maxHeight: 200, overflowY: 'auto' }}>
          <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Assign events
          </p>
          {allEvents.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>
              No events yet — add events in the Events tab first.
            </p>
          ) : (
            allEvents.map(ev => (
              <label key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={ev.swimlane === name}
                  onChange={e => onEventAssign(ev.id, e.target.checked ? name : null)}
                  style={{ accentColor: 'var(--accent)', flexShrink: 0 }}
                />
                <span style={{ fontSize: 12, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ev.title}
                </span>
              </label>
            ))
          )}
        </div>
      )}
    </li>
  );
};

const isMac = /mac/i.test(navigator.platform);

// ─── TIMELINE EDITOR PAGE ─────────────────────────────────────────────────────
//
//  Two-panel layout: left sidebar (Events / Design tabs) + live preview canvas.
//  All changes are local until the user clicks Save.
//
//  Props:
//    timeline         {object}    the timeline being edited
//    onSave           {function}  called with the updated timeline object
//    onBack           {function}  navigates back to the dashboard
//    currentUser      {object}    the logged-in user { id, name, avatar }
//    collaboratorRole {string}    'editor' | 'viewer' | null (null = owner)
//

const TimelineEditor = ({ timeline: initial, onSave, onBack, theme, onThemeToggle, currentUser, collaboratorRole, onNotificationClick, autoOpenCsvImport, onAutoOpenCsvImportDone }) => {

  const isOwner  = !collaboratorRole || collaboratorRole === 'owner';
  const isEditor = collaboratorRole === 'editor';
  const isViewer = collaboratorRole === 'viewer';
  const canEdit  = isOwner || isEditor;

  const isMobile = useIsMobile();
  const [mobileTab,       setMobileTab]       = useState('events'); // 'events' | 'design' | 'preview' | 'more'
  const mobileTouchStartX = useRef(null);
  const mobileTouchStartY = useRef(null);

  const [tl,              setTl]              = useState({ privacy: 'public', password: null, ...initial });
  const [tab,             setTab]             = useState('events'); // 'events' | 'design' | 'history' | 'collaborators'
  const [showEventForm,   setShowEventForm]   = useState(false);
  const [editingEvent,    setEditingEvent]    = useState(null);
  const [selectedEvent,   setSelectedEvent]   = useState(null);
  const [saved,           setSaved]           = useState(false);
  const [isDirty,         setIsDirty]         = useState(false);
  const [showHints,       setShowHints]       = useState(
    canEdit && !localStorage.getItem('chronos_editor_toured')
  );
  const [dismissedHints,  setDismissedHints]  = useState(new Set());
  const [showLeaveWarning,setShowLeaveWarning]= useState(false);
  const [privacyPassword,   setPrivacyPassword]   = useState('');
  const [privacyError,      setPrivacyError]      = useState('');
  const [linkCopied,        setLinkCopied]        = useState(false);
  const [descImgUploading,  setDescImgUploading]  = useState(false);

  // ── Drag-and-drop state ───────────────────────────────────────────────────
  const [activeId, setActiveId] = useState(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ── Version history state ─────────────────────────────────────────────────
  const [versions,        setVersions]        = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [previewVersion,  setPreviewVersion]  = useState(null);
  const [previewColors,   setPreviewColors]   = useState(null);
  const [restoreTarget,   setRestoreTarget]   = useState(null);
  const [restorePreLabel, setRestorePreLabel] = useState('');
  const [restoring,       setRestoring]       = useState(false);
  const [editingLabelId,  setEditingLabelId]  = useState(null);
  const [inlineLabelText, setInlineLabelText] = useState('');

  // ── Collaboration state ───────────────────────────────────────────────────
  const [collaborators,    setCollaborators]    = useState([]);
  const [pendingInvites,   setPendingInvites]   = useState([]);
  const [collabLoading,    setCollabLoading]    = useState(false);
  const [inviteEmail,      setInviteEmail]      = useState('');
  const [inviteRole,       setInviteRole]       = useState('editor');
  const [inviteSending,    setInviteSending]    = useState(false);
  const [inviteFeedback,   setInviteFeedback]   = useState(null); // { type: 'success'|'error', msg }
  const [removingUserId,   setRemovingUserId]   = useState(null);
  const [updatingRoleId,   setUpdatingRoleId]   = useState(null);
  const [cancellingInvId,  setCancellingInvId]  = useState(null);
  const [pendingTransfer,     setPendingTransfer]     = useState(null); // { toUserId, toUserName, expiresAt }
  const [showTransferModal,   setShowTransferModal]   = useState(false);
  const [transferToUserId,    setTransferToUserId]    = useState('');
  const [transferReason,      setTransferReason]      = useState('');
  const [transferConfirmed,   setTransferConfirmed]   = useState(false);
  const [transferError,       setTransferError]       = useState('');
  const [initiatingTransfer,  setInitiatingTransfer]  = useState(false);
  const [cancellingTransfer,  setCancellingTransfer]  = useState(false);

  // ── Collaboration activity state ──────────────────────────────────────────
  const [activity,       setActivity]       = useState([]);
  const [activityHasMore,setActivityHasMore]= useState(false);
  const [activityLoading,setActivityLoading]= useState(false);

  // ── Discussion thread state ───────────────────────────────────────────────
  const [discussions,      setDiscussions]      = useState([]);
  const [discLoading,      setDiscLoading]      = useState(false);
  const [discNewBody,      setDiscNewBody]      = useState('');
  const [discPosting,      setDiscPosting]      = useState(false);
  const [discReplyingTo,   setDiscReplyingTo]   = useState(null);  // commentId
  const [discReplyBody,    setDiscReplyBody]     = useState('');
  const [discReplyPosting, setDiscReplyPosting] = useState(false);
  const [discDeleteId,     setDiscDeleteId]      = useState(null); // commentId being deleted
  const [discDeleteReply,  setDiscDeleteReply]  = useState(null); // { commentId, replyId }
  const [discError,        setDiscError]         = useState('');

  // ── Import / Export state ─────────────────────────────────────────────────
  const [showCSVImport,   setShowCSVImport]   = useState(false);
  const [showGEDCOMImport,setShowGEDCOMImport]= useState(false);
  const [importInProgress,setImportInProgress]= useState(false);
  const [icalExporting,      setIcalExporting]      = useState(false);
  const [carouselExporting,  setCarouselExporting]  = useState(false);
  const [exportDropOpen,     setExportDropOpen]     = useState(false);
  const [undoImport,      setUndoImport]      = useState(null); // { snapshotVersionId, imported }
  const undoTimerRef = useRef(null);
  const [otherEditors,     setOtherEditors]     = useState([]);

  // ── Chapter management state ──────────────────────────────────────────────
  const [chapterAssignTarget, setChapterAssignTarget] = useState(null);
  const [renamingChapter,     setRenamingChapter]     = useState(null);
  const [renameValue,         setRenameValue]         = useState('');
  const [newChapterName,      setNewChapterName]      = useState('');
  const [activeChapterId,     setActiveChapterId]     = useState(null);

  // ── Swimlane management state ─────────────────────────────────────────────
  const [swimlaneAssignTarget, setSwimlaneAssignTarget] = useState(null);
  const [renamingSwimlane,     setRenamingSwimlane]     = useState(null);
  const [renameSwimlaneValue,  setRenameSwimlaneValue]  = useState('');
  const [newSwimlaneName,      setNewSwimlaneName]      = useState('');
  const [activeSwimlaneId,     setActiveSwimlaneId]     = useState(null);

  // ── Tree management state ─────────────────────────────────────────────────
  const [treeExpandedIds, setTreeExpandedIds] = useState(new Set());

  const descImgInputRef = useRef(null);
  const eventListRef    = useRef(null);
  // Debounce timer for auto-saving design-tab changes (eras, band labels…)
  // so typing in a label/date doesn't fire one PUT per keystroke.
  const designSaveTimerRef = useRef(null);
  const kbRef           = useRef({});
  const exportDropRef   = useRef(null);

  useEffect(() => {
    if (autoOpenCsvImport) {
      setShowCSVImport(true);
      onAutoOpenCsvImportDone?.();
    }
  }, [autoOpenCsvImport]);

  // ── Keyboard shortcut state ───────────────────────────────────────────────
  const [focusedEventId,     setFocusedEventId]     = useState(null);
  const [deleteConfirmId,    setDeleteConfirmId]     = useState(null);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);

  const update = (patch) => {
    if (isViewer) return;
    setTl(prev => ({ ...prev, ...patch }));
    setIsDirty(true);
  };

  const handleImported = useCallback((result) => {
    // Update local event list and mark dirty so the user knows to save
    setTl(prev => ({ ...prev, events: result.events }));
    setIsDirty(true);

    // Show undo toast — auto-dismiss after 10 s
    clearTimeout(undoTimerRef.current);
    setUndoImport({ snapshotVersionId: result.snapshotVersionId, imported: result.imported });
    undoTimerRef.current = setTimeout(() => setUndoImport(null), 10000);
  }, []);

  const handleUndoImport = async () => {
    if (!undoImport) return;
    clearTimeout(undoTimerRef.current);
    setUndoImport(null);
    try {
      const restored = await Storage.restoreVersion(tl.id, undoImport.snapshotVersionId, 'Undo import');
      setTl(prev => ({ ...prev, events: restored.events }));
      setIsDirty(true);
    } catch {
      // silent — user can restore from version history manually
    }
  };

  const handleExportJSON = () => Storage.exportTimelineJSON(tl);

  const handleExportIcal = async () => {
    setIcalExporting(true);
    try {
      await Storage.exportIcal(tl.id, tl.title);
    } catch {
      // silent — browser download failures are rare
    } finally {
      setIcalExporting(false);
    }
  };

  const handleCarouselExport = async () => {
    setCarouselExporting(true);
    try {
      await Storage.exportCarousel(tl.id, tl.title);
    } catch (err) {
      alert(`Carousel export failed: ${err.message}`);
    } finally {
      setCarouselExporting(false);
    }
  };

  const handleDescImgUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setDescImgUploading(true);
    try {
      const url = await Storage.uploadImage(file, 'description-image');
      const imgTag = `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:6px;margin:6px auto;display:block;" />`;
      update({ description: (tl.description || '') + imgTag });
    } catch (err) {
      console.error('Description image upload error:', err);
    } finally {
      setDescImgUploading(false);
    }
  };

  const removeDescImg = (index) => {
    const imgs = ((tl.description || '').match(/<img\b[^>]*\/?>/gi) || []);
    imgs.splice(index, 1);
    update({ description: stripHtmlKeepSpaces(tl.description || '') + imgs.join('') });
  };

  // ── Event CRUD ────────────────────────────────────────────────────────────

  // Builds the save payload exactly as handleSave does, then persists immediately.
  // Used for auto-save on event add/edit/duplicate so unsaved work isn't lost.
  const autoSave = (updatedTl) => {
    if (isViewer) return;
    const isPrivate = updatedTl.privacy === 'private' || updatedTl.privacy === 'password-protected';
    const toSave = isPrivate && privacyPassword
      ? { ...updatedTl, privacy: 'password-protected', password: privacyPassword }
      : updatedTl;
    onSave(toSave);
    setIsDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const saveEvent = (ev) => {
    const exists = tl.events.find(e => e.id === ev.id);
    let updatedEvents;
    if (exists) {
      updatedEvents = tl.events.map(e => e.id === ev.id ? { ...ev, sortOrder: e.sortOrder ?? 0 } : e);
    } else {
      const maxSort = tl.events.reduce((m, e) => Math.max(m, e.sortOrder ?? 0), -1);
      updatedEvents = [...tl.events, { ...ev, sortOrder: maxSort + 1 }];
    }
    const updatedTl = { ...tl, events: updatedEvents };
    setTl(updatedTl);
    setShowEventForm(false);
    setEditingEvent(null);
    autoSave(updatedTl);
  };

  const deleteEvent = (id) => {
    update({ events: tl.events.filter(e => e.id !== id) });
    setSelectedEvent(null);
  };

  const duplicateEvent = (ev) => {
    const updatedTl = { ...tl, events: [...tl.events, { ...ev, id: uid(), title: ev.title + ' (copy)' }] };
    setTl(updatedTl);
    autoSave(updatedTl);
  };

  const openAddEvent   = ()   => { if (!canEdit) return; setEditingEvent(null); setShowEventForm(true); };
  const openEditEvent  = (ev) => { setEditingEvent(ev);   setShowEventForm(true); };
  const closeEventForm = ()   => { setShowEventForm(false); setEditingEvent(null); };

  // ── Save ─────────────────────────────────────────────────────────────────

  const handleSave = () => {
    if (isViewer) return;
    const isPrivate = tl.privacy === 'private' || tl.privacy === 'password-protected';
    if (isPrivate && !tl.hasPassword && !privacyPassword) {
      setPrivacyError('Set a password to make this timeline private.');
      setTab('design');
      return;
    }
    // Send plaintext password — the server bcrypts it.
    // Always save password-protected timelines with the correct privacy value.
    const toSave = isPrivate && privacyPassword
      ? { ...tl, privacy: 'password-protected', password: privacyPassword }
      : isPrivate
      ? { ...tl, privacy: 'password-protected' }
      : tl;
    onSave(toSave);
    if (privacyPassword) {
      setPrivacyPassword('');
      setTl(prev => ({ ...prev, hasPassword: true }));
    }
    setIsDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    if (showHints) {
      localStorage.setItem('chronos_editor_toured', '1');
      setShowHints(false);
    }
  };

  const handleBack = () => {
    if (isDirty) { setShowLeaveWarning(true); return; }
    onBack();
  };

  // ── Event ordering ────────────────────────────────────────────────────────

  // The displayed order in the sidebar list — used as the DnD source of truth.
  const displaySorted = [...tl.events].sort((a, b) => {
    if (tl.manualOrder) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    const d = (a.startDate || "").localeCompare(b.startDate || "");
    if (d !== 0) return d;
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIdx = displaySorted.findIndex(e => e.id === active.id);
    const newIdx = displaySorted.findIndex(e => e.id === over.id);
    const reordered = arrayMove(displaySorted, oldIdx, newIdx).map((ev, i) => ({ ...ev, sortOrder: i }));
    const byId = Object.fromEntries(reordered.map(e => [e.id, e]));
    update({ events: tl.events.map(e => byId[e.id] ?? e), manualOrder: true });
  };

  const resetToChronological = () => {
    const chronological = [...tl.events]
      .sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""))
      .map((ev, i) => ({ ...ev, sortOrder: i }));
    update({ events: chronological, manualOrder: false });
  };

  const activeEvent = activeId ? tl.events.find(e => e.id === activeId) : null;

  // ── Chapter management ────────────────────────────────────────────────────

  // Normalize any chapter entry (backward compat: string -> object)
  const normChapterEntry = (c) =>
    typeof c === 'string' ? { name: c } : (c && typeof c === 'object' ? c : { name: '' });

  // Returns normalized array of chapter objects (preserving metadata)
  const getNormChapters = () =>
    (tl.chapters || []).map(normChapterEntry).filter(c => c.name);

  // Returns string[] of chapter names — used for DND items and EventForm chapter list
  const getChapterOrder = () => {
    const seen = new Set();
    const order = [];
    getNormChapters().forEach(c => { order.push(c.name); seen.add(c.name); });
    tl.events.forEach(e => { if (e.chapter && !seen.has(e.chapter)) { order.push(e.chapter); seen.add(e.chapter); } });
    return order;
  };

  const addChapter = () => {
    const name = newChapterName.trim();
    if (!name) return;
    const existing = getNormChapters();
    if (existing.some(c => c.name.toLowerCase() === name.toLowerCase())) return;
    update({ chapters: [...existing, { name }] });
    setNewChapterName('');
  };

  const renameChapter = (oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) { setRenamingChapter(null); return; }
    update({
      chapters: getNormChapters().map(c => c.name === oldName ? { ...c, name: trimmed } : c),
      events:   tl.events.map(e => e.chapter === oldName ? { ...e, chapter: trimmed } : e),
    });
    setRenamingChapter(null);
  };

  const deleteChapter = (name) => {
    update({
      chapters: getNormChapters().filter(c => c.name !== name),
      events:   tl.events.map(e => e.chapter === name ? { ...e, chapter: null } : e),
    });
    if (chapterAssignTarget === name) setChapterAssignTarget(null);
  };

  const updateChapterMeta = (name, patch) => {
    update({ chapters: getNormChapters().map(c => c.name === name ? { ...c, ...patch } : c) });
  };

  const handleChapterDragEnd = ({ active, over }) => {
    setActiveChapterId(null);
    if (!over || active.id === over.id) return;
    const chObjs = getNormChapters();
    const names  = chObjs.map(c => c.name);
    const oldIdx = names.indexOf(active.id);
    const newIdx = names.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    update({ chapters: arrayMove(chObjs, oldIdx, newIdx) });
  };

  const handleAddEventToChapter = (chapterName) => {
    setEditingEvent({ chapter: chapterName });
    setShowEventForm(true);
  };

  // ── Swimlane handlers ────────────────────────────────────────────────────

  const getSwimlaneOrder = () => {
    const seen = new Set();
    const order = [];
    (tl.swimlanes || []).forEach(s => { if (s) { order.push(s); seen.add(s); } });
    tl.events.forEach(e => { if (e.swimlane && !seen.has(e.swimlane)) { order.push(e.swimlane); seen.add(e.swimlane); } });
    return order;
  };

  const addSwimlane = () => {
    const name = newSwimlaneName.trim();
    if (!name) return;
    const existing = getSwimlaneOrder();
    if (existing.map(s => s.toLowerCase()).includes(name.toLowerCase())) return;
    update({ swimlanes: [...existing, name] });
    setNewSwimlaneName('');
  };

  const renameSwimlane = (oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) { setRenamingSwimlane(null); return; }
    update({
      swimlanes: getSwimlaneOrder().map(s => s === oldName ? trimmed : s),
      events:    tl.events.map(e => e.swimlane === oldName ? { ...e, swimlane: trimmed } : e),
      swimlaneColors: Object.fromEntries(
        Object.entries(tl.swimlaneColors || {}).map(([k, v]) => [k === oldName ? trimmed : k, v])
      ),
    });
    setRenamingSwimlane(null);
  };

  const deleteSwimlane = (name) => {
    update({
      swimlanes: getSwimlaneOrder().filter(s => s !== name),
      events:    tl.events.map(e => e.swimlane === name ? { ...e, swimlane: null } : e),
    });
    if (swimlaneAssignTarget === name) setSwimlaneAssignTarget(null);
  };

  const setSwimlaneColor = (name, color) => {
    update({ swimlaneColors: { ...(tl.swimlaneColors || {}), [name]: color } });
  };

  const handleSwimlaneDragEnd = ({ active, over }) => {
    setActiveSwimlaneId(null);
    if (!over || active.id === over.id) return;
    const order = getSwimlaneOrder();
    const oldIdx = order.indexOf(active.id);
    const newIdx = order.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    update({ swimlanes: arrayMove(order, oldIdx, newIdx) });
  };

  // ── Tree handlers ─────────────────────────────────────────────────────────

  const getTreeDescendants = (id, evList) => {
    const result = new Set();
    const walk = (nId) => {
      evList.filter(e => e.parentEventId === nId).forEach(e => { result.add(e.id); walk(e.id); });
    };
    walk(id);
    return result;
  };

  const setParentEvent = (eventId, newParentId) => {
    if (newParentId && getTreeDescendants(eventId, tl.events).has(newParentId)) return; // prevent cycles
    update({ events: tl.events.map(e => e.id === eventId ? { ...e, parentEventId: newParentId || null } : e) });
  };

  const handleAutoDetectBranches = () => {
    const groups = {};
    tl.events.forEach(ev => {
      const key = ev.swimlane || ev.chapter || null;
      if (key) { if (!groups[key]) groups[key] = []; groups[key].push(ev); }
    });
    Object.values(groups).forEach(g => g.sort((a, b) => (a.startDate || "").localeCompare(b.startDate || "")));
    const updatedEvents = tl.events.map(ev => {
      const key = ev.swimlane || ev.chapter || null;
      if (!key) return ev;
      const group = groups[key];
      const idx = group.findIndex(g => g.id === ev.id);
      return { ...ev, parentEventId: idx <= 0 ? null : group[idx - 1].id };
    });
    update({ events: updatedEvents });
  };

  // Depth-first ordered list of all events for the Tree outline
  const getTreeOutline = () => {
    const nm = {};
    tl.events.forEach(ev => { nm[ev.id] = { ev, children: [] }; });
    const roots = [];
    tl.events.forEach(ev => {
      if (ev.parentEventId && nm[ev.parentEventId]) nm[ev.parentEventId].children.push(ev.id);
      else roots.push(ev.id);
    });
    const byDate = (a, b) => ((nm[a].ev.startDate || "").localeCompare(nm[b].ev.startDate || ""));
    roots.sort(byDate);
    Object.values(nm).forEach(n => n.children.sort(byDate));

    const result = [];
    const walk = (id, depth) => {
      result.push({ id, ev: nm[id].ev, depth, hasChildren: nm[id].children.length > 0 });
      if (treeExpandedIds.has(id)) nm[id].children.forEach(c => walk(c, depth + 1));
    };
    roots.forEach(id => walk(id, 0));
    return { outline: result, nm };
  };

  // ── Version history ───────────────────────────────────────────────────────

  useEffect(() => {
    if (tab !== 'history' || !tl.id) return;
    let cancelled = false;
    setVersionsLoading(true);
    Storage.getVersions(tl.id)
      .then(({ versions: v }) => { if (!cancelled) { setVersions(v); setVersionsLoading(false); } })
      .catch(() => { if (!cancelled) setVersionsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, tl.id]);

  // ── Collaborators loading ─────────────────────────────────────────────────

  useEffect(() => {
    if (tab !== 'collaborators' || !tl.id || !isOwner) return;
    let cancelled = false;
    setCollabLoading(true);
    Storage.getCollaborators(tl.id)
      .then(({ collaborators: c, pendingInvites: p, pendingTransfer: pt }) => {
        if (!cancelled) { setCollaborators(c); setPendingInvites(p); setPendingTransfer(pt ?? null); setCollabLoading(false); }
      })
      .catch(() => { if (!cancelled) setCollabLoading(false); });
    return () => { cancelled = true; };
  }, [tab, tl.id]);

  // ── Collaboration activity loading ────────────────────────────────────────

  useEffect(() => {
    if (tab !== 'collaborators' || !tl.id || !isOwner) return;
    let cancelled = false;
    setActivityLoading(true);
    Storage.getActivity(tl.id)
      .then(({ activity: a, hasMore }) => {
        if (!cancelled) { setActivity(a); setActivityHasMore(hasMore); setActivityLoading(false); }
      })
      .catch(() => { if (!cancelled) setActivityLoading(false); });
    return () => { cancelled = true; };
  }, [tab, tl.id]);

  // ── Heartbeat — tells the server this user is actively editing ────────────

  useEffect(() => {
    if (!tl.id || !canEdit) return;
    const tick = async () => {
      try {
        const { others } = await Storage.heartbeat(tl.id);
        setOtherEditors(others || []);
      } catch {}
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [tl.id, canEdit]);

  useEffect(() => { setFocusedEventId(null); }, [tab]);

  useEffect(() => {
    const isInputTarget = (target) => {
      const tag = target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
    };

    const onKeyDown = (e) => {
      const {
        tl, canEdit, focusedEventId, handleSave, openAddEvent, duplicateEvent, update,
        showEventForm, previewVersion, restoreTarget, showLeaveWarning, selectedEvent,
        showShortcutsModal, deleteConfirmId, setShowShortcutsModal, setDeleteConfirmId,
      } = kbRef.current;

      const mod = isMac ? e.metaKey : e.ctrlKey;
      const anyModalOpen = showEventForm || !!previewVersion || !!restoreTarget ||
        showLeaveWarning || !!selectedEvent || showShortcutsModal || !!deleteConfirmId;

      // Ctrl/Cmd+S — save (always preventDefault to block browser save dialog)
      if (mod && e.key === 's') {
        e.preventDefault();
        if (!anyModalOpen) handleSave();
        return;
      }

      // All remaining shortcuts are blocked when a modal is open or when typing
      if (anyModalOpen || isInputTarget(e.target)) return;

      if (mod && e.key === 'e') { e.preventDefault(); if (canEdit) openAddEvent(); return; }

      if (mod && e.key === 'l') {
        e.preventDefault();
        if (canEdit && tl.orientation !== 'map') {
          const next = { horizontal: 'vertical', vertical: 'zigzag', zigzag: 'storyline', storyline: 'newspaper', newspaper: 'swimlane', swimlane: 'tree', tree: 'horizontal' };
          update({ orientation: next[tl.orientation] || 'vertical' });
        }
        return;
      }

      if (mod && e.key === 'd') {
        e.preventDefault();
        if (canEdit && focusedEventId) {
          const ev = tl.events.find(ev => ev.id === focusedEventId);
          if (ev) duplicateEvent(ev);
        }
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && focusedEventId) {
        e.preventDefault();
        if (canEdit) setDeleteConfirmId(focusedEventId);
        return;
      }

      if (e.key === '?') { setShowShortcutsModal(true); return; }

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const list = eventListRef.current;
        if (!list?.contains(document.activeElement)) return;
        e.preventDefault();
        const cards = Array.from(list.querySelectorAll('[data-event-card]'));
        const idx = cards.indexOf(document.activeElement);
        if (idx === -1) { cards[0]?.focus(); return; }
        const next = e.key === 'ArrowDown'
          ? Math.min(idx + 1, cards.length - 1)
          : Math.max(idx - 1, 0);
        cards[next]?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []); // stable listener — always reads latest values from kbRef.current

  // ── Close export dropdown on outside click ──────────────────────────────────
  useEffect(() => {
    if (!exportDropOpen) return;
    const handler = (e) => {
      if (exportDropRef.current && !exportDropRef.current.contains(e.target)) setExportDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportDropOpen]);

  const openVersionPreview = (ver) => {
    Storage.getVersion(tl.id, ver.id)
      .then(({ version }) => setPreviewVersion(version))
      .catch(err => console.error('Failed to load version preview:', err.message));
  };

  const confirmRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const restored = await Storage.restoreVersion(tl.id, restoreTarget.id, restorePreLabel.trim() || null);
      setTl(prev => ({ ...prev, ...restored }));
      setIsDirty(false);
      setRestoreTarget(null);
      setRestorePreLabel('');
      const { versions: v } = await Storage.getVersions(tl.id);
      setVersions(v);
    } catch (err) {
      console.error('Restore failed:', err.message);
    } finally {
      setRestoring(false);
    }
  };

  const handleSaveLabel = async (versionId, label) => {
    try {
      await Storage.labelVersion(tl.id, versionId, label.trim() || null);
      setVersions(prev => prev.map(v =>
        v.id === versionId ? { ...v, label: label.trim() || null } : v
      ));
      setEditingLabelId(null);
    } catch (err) {
      console.error('Label update failed:', err.message);
    }
  };

  // ── Collaboration handlers ────────────────────────────────────────────────

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteSending(true);
    setInviteFeedback(null);
    try {
      await Storage.inviteCollaborator(tl.id, inviteEmail.trim(), inviteRole);
      setInviteFeedback({ type: 'success', msg: `Invite sent to ${inviteEmail.trim()}` });
      setInviteEmail('');
      const { collaborators: c, pendingInvites: p, pendingTransfer: pt } = await Storage.getCollaborators(tl.id);
      setCollaborators(c);
      setPendingInvites(p);
      setPendingTransfer(pt ?? null);
    } catch (err) {
      setInviteFeedback({ type: 'error', msg: err.message });
    } finally {
      setInviteSending(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRoleId(userId);
    try {
      await Storage.updateCollaboratorRole(tl.id, userId, newRole);
      setCollaborators(prev => prev.map(c => c.id === userId ? { ...c, role: newRole } : c));
    } catch (err) {
      console.error('Role update failed:', err.message);
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleRemoveCollaborator = async (userId) => {
    setRemovingUserId(userId);
    try {
      await Storage.removeCollaborator(tl.id, userId);
      setCollaborators(prev => prev.filter(c => c.id !== userId));
    } catch (err) {
      console.error('Remove collaborator failed:', err.message);
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    setCancellingInvId(inviteId);
    try {
      await Storage.cancelInvite(tl.id, inviteId);
      setPendingInvites(prev => prev.filter(p => p.id !== inviteId));
    } catch (err) {
      console.error('Cancel invite failed:', err.message);
    } finally {
      setCancellingInvId(null);
    }
  };

  const handleInitiateTransfer = async () => {
    if (!transferToUserId || !transferConfirmed) return;
    setInitiatingTransfer(true);
    setTransferError('');
    try {
      await Storage.initiateOwnershipTransfer(tl.id, transferToUserId, transferReason.trim() || undefined);
      const { collaborators: c, pendingInvites: p, pendingTransfer: pt } = await Storage.getCollaborators(tl.id);
      setCollaborators(c); setPendingInvites(p); setPendingTransfer(pt ?? null);
      setShowTransferModal(false);
      setTransferToUserId('');
      setTransferReason('');
      setTransferConfirmed(false);
    } catch (err) {
      setTransferError(err.message);
    } finally {
      setInitiatingTransfer(false);
    }
  };

  const handleCancelTransfer = async () => {
    setCancellingTransfer(true);
    try {
      await Storage.cancelOwnershipTransfer(tl.id);
      setPendingTransfer(null);
    } catch (err) {
      console.error('Cancel transfer failed:', err.message);
    } finally {
      setCancellingTransfer(false);
    }
  };

  // ── Activity helpers ──────────────────────────────────────────────────────

  const handleLoadMoreActivity = async () => {
    if (activityLoading || !activityHasMore || activity.length === 0) return;
    setActivityLoading(true);
    try {
      const oldest = activity[activity.length - 1]?.createdAt;
      const { activity: more, hasMore } = await Storage.getActivity(tl.id, { before: oldest });
      setActivity(prev => [...prev, ...more]);
      setActivityHasMore(hasMore);
    } catch (err) {
      console.error('Load more activity failed:', err.message);
    } finally {
      setActivityLoading(false);
    }
  };

  const describeActivity = (item) => {
    const p = item.payload || {};
    switch (item.actionType) {
      case 'timeline_settings_changed':
        return `updated ${(p.changes || []).join(', ') || 'timeline settings'}`;
      case 'invite_sent':
        return `invited ${p.email} as ${p.role}`;
      case 'invite_cancelled':
        return `cancelled invite to ${p.email}`;
      case 'collaborator_joined':
        return `joined as ${p.role}`;
      case 'role_changed':
        return `changed ${p.collaboratorName}'s role from ${p.oldRole} to ${p.newRole}`;
      case 'collaborator_removed':
        return `removed ${p.collaboratorName}`;
      case 'collaborator_left':
        return `left the timeline`;
      case 'ownership_transferred':
        return `transferred ownership to ${p.toName}`;
      default:
        return item.actionType.replace(/_/g, ' ');
    }
  };

  const relativeTime = (ts) => {
    const diff = Date.now() - ts;
    if (diff < 60000)   return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(ts).toLocaleDateString();
  };

  // ── Discussion handlers ───────────────────────────────────────────────────

  useEffect(() => {
    if (tab !== 'discussions' || !tl.id) return;
    let cancelled = false;
    setDiscLoading(true);
    Storage.getDiscussions(tl.id)
      .then(({ comments }) => { if (!cancelled) { setDiscussions(comments); setDiscLoading(false); } })
      .catch(() => { if (!cancelled) setDiscLoading(false); });
    return () => { cancelled = true; };
  }, [tab, tl.id]);

  const handlePostDiscussionComment = async () => {
    const body = discNewBody.trim();
    if (!body || discPosting) return;
    setDiscPosting(true);
    setDiscError('');
    try {
      const { comment } = await Storage.postDiscussionComment(tl.id, body);
      setDiscussions(prev => [...prev, comment]);
      setDiscNewBody('');
    } catch (err) {
      setDiscError(err.message);
    } finally {
      setDiscPosting(false);
    }
  };

  const handlePostDiscussionReply = async (commentId) => {
    const body = discReplyBody.trim();
    if (!body || discReplyPosting) return;
    setDiscReplyPosting(true);
    setDiscError('');
    try {
      const { reply } = await Storage.postDiscussionReply(tl.id, commentId, body);
      setDiscussions(prev => prev.map(c =>
        c.id === commentId ? { ...c, replies: [...c.replies, reply] } : c
      ));
      setDiscReplyingTo(null);
      setDiscReplyBody('');
    } catch (err) {
      setDiscError(err.message);
    } finally {
      setDiscReplyPosting(false);
    }
  };

  const handleDeleteDiscussionComment = async (commentId) => {
    setDiscDeleteId(commentId);
    try {
      const { softDeleted } = await Storage.deleteDiscussionComment(tl.id, commentId);
      if (softDeleted) {
        setDiscussions(prev => prev.map(c =>
          c.id === commentId ? { ...c, body: null, isDeleted: true } : c
        ));
      } else {
        setDiscussions(prev => prev.filter(c => c.id !== commentId));
      }
    } catch (err) {
      console.error('Delete discussion comment failed:', err.message);
    } finally {
      setDiscDeleteId(null);
    }
  };

  const handleDeleteDiscussionReply = async (commentId, replyId) => {
    setDiscDeleteReply({ commentId, replyId });
    try {
      await Storage.deleteDiscussionReply(tl.id, commentId, replyId);
      setDiscussions(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, replies: c.replies.filter(r => r.id !== replyId) }
          : c
      ).filter(c => !(c.isDeleted && c.replies.length === 0)));
    } catch (err) {
      console.error('Delete discussion reply failed:', err.message);
    } finally {
      setDiscDeleteReply(null);
    }
  };

  // ── Derived date range ────────────────────────────────────────────────────

  const sorted  = [...tl.events].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
  const minDate = sorted.length ? sorted[0].startDate : null;
  const maxDate = sorted.length ? sorted[sorted.length - 1].endDate : null;

  // Always-fresh snapshot so the stable keydown listener reads current values
  kbRef.current = {
    tl, canEdit, focusedEventId, handleSave, openAddEvent, duplicateEvent, update,
    showEventForm, previewVersion, restoreTarget, showLeaveWarning, selectedEvent,
    showShortcutsModal, deleteConfirmId, setShowShortcutsModal, setDeleteConfirmId,
  };

  const deleteConfirmEvent = deleteConfirmId ? tl.events.find(e => e.id === deleteConfirmId) : null;

  const tabs = [
    ['events',  'Events'],
    ['design',  'Design'],
    ['history', 'History'],
    ...(tl.orientation === 'storyline' && canEdit ? [['chapters', 'Chapters']] : []),
    ...(tl.orientation === 'swimlane' && canEdit ? [['swimlanes', 'Swimlanes']] : []),
    ...((tl.orientation === 'tree' || tl.events.some(e => e.parentEventId)) && canEdit ? [['tree', 'Tree']] : []),
    ...(isOwner ? [['collaborators', 'Collaborators']] : []),
    ['discussions', 'Discussions'],
  ];

  // ── Mobile tab helpers ───────────────────────────────────────────────────────

  // Map mobile tab to the desktop sidebar tab so panel content stays in sync
  const setMobileTabSynced = (mt) => {
    setMobileTab(mt);
    if (mt === 'events')  setTab('events');
    if (mt === 'design')  setTab('design');
    if (mt === 'more')    setTab('history');
  };

  const MOBILE_TAB_ORDER = ['events', 'design', 'preview', 'more'];

  const onMobileTouchStart = (e) => {
    // Don't track swipes that start inside the timeline preview canvas
    if (e.target.closest('.ht-root, .ht-scroll')) {
      mobileTouchStartX.current = null;
      return;
    }
    mobileTouchStartX.current = e.touches[0].clientX;
    mobileTouchStartY.current = e.touches[0].clientY;
  };

  const onMobileTouchEnd = (e) => {
    if (mobileTouchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - mobileTouchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - mobileTouchStartY.current);
    mobileTouchStartX.current = null;
    mobileTouchStartY.current = null;
    if (Math.abs(dx) < 50 || dy > 30) return; // not a horizontal swipe
    const cur = MOBILE_TAB_ORDER.indexOf(mobileTab);
    if (dx < 0 && cur < MOBILE_TAB_ORDER.length - 1) setMobileTabSynced(MOBILE_TAB_ORDER[cur + 1]);
    if (dx > 0 && cur > 0)                            setMobileTabSynced(MOBILE_TAB_ORDER[cur - 1]);
  };

  return (
    <div
      style={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg)' }}
      className={isMobile ? `editor-root mobile-mode mobile-tab-${mobileTab}` : 'editor-root'}
      onTouchStart={isMobile ? onMobileTouchStart : undefined}
      onTouchEnd={isMobile ? onMobileTouchEnd : undefined}
    >

      {/* ── Header ── */}
      <header
        className="timeline-header"
        style={{
          background: 'var(--surface)', borderBottom: '1px solid var(--border)',
          padding: '12px 24px', display: 'flex', flexDirection: 'column', gap: 6,
          flexShrink: 0, zIndex: 100,
        }}
      >
        {/* ── Nav row ── */}
        <div className="editor-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* My Dashboard — always visible */}
          <button className="btn-ghost btn-sm" onClick={handleBack} style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <Icon name="back" size={16} /> My Dashboard
          </button>

          {/* Switch Layout — always visible */}
          {!isViewer && tl.orientation !== 'map' && (() => {
            const nextOrient = { horizontal: 'vertical', vertical: 'zigzag', zigzag: 'storyline', storyline: 'newspaper', newspaper: 'swimlane', swimlane: 'tree', tree: 'filmstrip', filmstrip: 'spine', spine: 'horizontal' };
            const nextLabel  = { horizontal: 'Vertical',  vertical: 'Zigzag',  zigzag: 'Storyline', storyline: 'Newspaper', newspaper: 'Swimlane', swimlane: 'Tree', tree: 'Filmstrip', filmstrip: 'Spine', spine: 'Horizontal' };
            const next = nextOrient[tl.orientation] || 'vertical';
            return (
              <button
                onClick={() => update({ orientation: next })}
                className="btn-ghost btn-sm"
                aria-label={`Switch to ${nextLabel[tl.orientation]} layout`}
                title={`Switch Layout (${isMac ? '⌘' : 'Ctrl+'}L)`}
                style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <Icon name={next} size={14} />
                {isMobile ? 'Switch' : `Switch to ${nextLabel[tl.orientation]}`}
              </button>
            );
          })()}

          <div style={{ flex: 1 }} />

          {/* Desktop-only: Theme + Keyboard + Save */}
          {!isMobile && (<>
            <button
              className="btn-ghost btn-sm"
              onClick={onThemeToggle}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, padding: 0, flexShrink: 0 }}
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
            </button>
            <button
              className="btn-ghost btn-sm"
              onClick={() => setShowShortcutsModal(true)}
              aria-label="Keyboard shortcuts"
              title="Keyboard Shortcuts (?)"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, padding: 0, flexShrink: 0 }}
            >
              <Icon name="keyboard" size={15} />
            </button>
            {!isViewer && (
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  className="btn-primary btn-sm"
                  onClick={handleSave}
                  aria-label={saved ? 'Changes saved' : 'Save timeline'}
                  title={`Save (${isMac ? '⌘' : 'Ctrl+'}S)`}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <Icon name="save" size={14} />
                  {saved ? '✓ Saved' : 'Save'}
                </button>
                {showHints && !dismissedHints.has('save') && (
                  <div className="editor-hint-dot editor-hint-dot--save" onClick={() => setDismissedHints(p => new Set([...p, 'save']))}>
                    <span className="editor-hint-tooltip editor-hint-tooltip--left">Save your changes — don't forget to save!</span>
                  </div>
                )}
              </div>
            )}
          </>)}

          {/* Mobile: icon-only Save button + hamburger for Theme/Keyboard/Notifications */}
          {isMobile && !isViewer && (
            <button
              className="btn-primary btn-sm"
              onClick={handleSave}
              aria-label={saved ? 'Changes saved' : 'Save timeline'}
              title={`Save (${isMac ? '⌘' : 'Ctrl+'}S)`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, padding: 0, flexShrink: 0, color: saved ? 'var(--accent)' : undefined }}
            >
              <Icon name="save" size={14} />
            </button>
          )}
          {isMobile && (
            <MobileMenu>
              {(close) => <>
                <button
                  onClick={() => { onThemeToggle(); close(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
                >
                  <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </button>
                <button
                  onClick={() => { setShowShortcutsModal(true); close(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
                >
                  <Icon name="keyboard" size={15} /> Shortcuts
                </button>
                {currentUser?.id && onNotificationClick && (
                  <div style={{ padding: '4px 16px' }}>
                    <NotificationBell userId={currentUser.id} onViewTimeline={onNotificationClick} onViewProfile={() => {}} />
                  </div>
                )}
              </>}
            </MobileMenu>
          )}
        </div>

        {/* ── Title row (below nav) ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <input
            value={tl.title}
            onChange={e => update({ title: e.target.value })}
            placeholder="Timeline Title"
            aria-label="Timeline title"
            readOnly={isViewer}
            style={{ background: 'transparent', border: 'none', fontSize: 18, fontFamily: 'var(--font-display)', color: 'var(--text)', fontWeight: 700, width: '100%', padding: 0, cursor: isViewer ? 'default' : 'text', minWidth: 0 }}
          />
          {isDirty && !saved && (
            <span className="editor-unsaved-dot" aria-label="Unsaved changes" title="Unsaved changes" />
          )}
        </div>

        <div className="editor-meta-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              value={stripHtmlKeepSpaces(tl.description || '')}
              onChange={e => {
                const imgs = ((tl.description || '').match(/<img\b[^>]*\/?>/gi) || []).join('');
                update({ description: e.target.value + imgs });
              }}
              placeholder="Add a description… (optional)"
              aria-label="Timeline description"
              readOnly={isViewer}
              style={{ background: 'transparent', border: 'none', fontSize: 13, color: 'var(--text2)', flex: 1, padding: 0, cursor: isViewer ? 'default' : 'text' }}
            />
            {!isViewer && (
              <button
                type="button"
                onClick={() => descImgInputRef.current?.click()}
                disabled={descImgUploading}
                title={descImgUploading ? 'Uploading…' : 'Add image to description'}
                style={{ background: 'none', border: 'none', cursor: descImgUploading ? 'default' : 'pointer', color: 'var(--text3)', padding: 4, display: 'flex', alignItems: 'center', opacity: descImgUploading ? 0.5 : 1, flexShrink: 0 }}
              >
                <Icon name="image" size={14} />
              </button>
            )}
          </div>
          {((tl.description || '').match(/<img\b[^>]*\/?>/gi) || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {((tl.description || '').match(/<img\b[^>]*\/?>/gi) || []).map((imgTag, i) => {
                const src = imgTag.match(/src="([^"]*)"/)?.[1];
                return src ? (
                  <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={src} alt="" style={{ height: 40, width: 'auto', maxWidth: 80, borderRadius: 4, border: '1px solid var(--border)', objectFit: 'cover', display: 'block' }} />
                    {!isViewer && (
                      <button
                        type="button"
                        onClick={() => removeDescImg(i)}
                        aria-label="Remove description image"
                        style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: 3, color: '#fff', fontSize: 9, padding: '1px 5px', cursor: 'pointer', lineHeight: 1.4 }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ) : null;
              })}
            </div>
          )}
          <input
            ref={descImgInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleDescImgUpload}
          />
        </div>

        <div className="editor-meta-row" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          {tl.category && (() => {
            const cat = TIMELINE_CATEGORIES.find(c => c.value === tl.category);
            return cat ? (
              <span aria-hidden="true" style={{ position: 'absolute', left: 10, fontSize: 14, pointerEvents: 'none', zIndex: 1 }}>
                {cat.emoji}
              </span>
            ) : null;
          })()}
          <select
            value={tl.category || ''}
            onChange={e => update({ category: e.target.value || null })}
            aria-label="Timeline category"
            disabled={isViewer}
            style={{
              paddingLeft: tl.category ? 30 : 10,
              paddingRight: 28, paddingTop: 6, paddingBottom: 6,
              border: '1px solid var(--border2)',
              borderRadius: 'var(--radius)',
              background: 'var(--surface)',
              fontSize: 13,
              color: tl.category ? 'var(--text)' : 'var(--text3)',
              cursor: isViewer ? 'default' : 'pointer',
              appearance: 'none', WebkitAppearance: 'none',
              backgroundImage: isViewer ? 'none' : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 10px center',
              minWidth: 160,
            }}
          >
            <option value="">No category</option>
            {TIMELINE_CATEGORY_GROUPS.map(group => (
              <optgroup key={group.label} label={group.label}>
                {group.values.map(val => {
                  const cat = TIMELINE_CATEGORIES.find(c => c.value === val);
                  return cat ? <option key={val} value={val}>{cat.emoji} {cat.label}</option> : null;
                })}
              </optgroup>
            ))}
          </select>
        </div>
      </header>

      {/* ── Viewer banner ── */}
      {isViewer && (
        <div style={{
          background: 'var(--accent)15', border: '1px solid var(--accent)40',
          borderRadius: 'var(--radius)', padding: '9px 16px', margin: '12px 24px 0',
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--accent)',
        }}>
          <Icon name="eye" size={14} />
          You have <strong style={{ marginLeft: 3 }}>view-only access</strong> to this timeline.
        </div>
      )}

      {/* ── Concurrent-edit banner ── */}
      {otherEditors.length > 0 && (
        <div style={{
          background: '#92400e22', border: '1px solid #92400e40',
          borderRadius: 'var(--radius)', padding: '9px 16px', margin: '8px 24px 0',
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#d97706',
        }}>
          <Icon name="users" size={14} />
          Also editing: <strong style={{ marginLeft: 3 }}>{otherEditors.map(e => e.name).join(', ')}</strong>
        </div>
      )}

      {/* ── Body: sidebar + preview ── */}
      <div
        className="editor-grid"
        style={{ display: 'grid', gridTemplateColumns: (tab === 'events' || tab === 'design') ? '320px 1fr' : '1fr', gridTemplateRows: '1fr', flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        {/* Sidebar */}
        <div
          className="editor-sidebar"
          style={{ background: 'var(--bg2)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
        >
          {/* Tabs */}
          <div role="tablist" aria-label="Editor sections" style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
            {tabs.map(([k, label]) => {
              const hintMap = {
                events: 'Add and manage your timeline events here',
                design: 'Customize colors, fonts, and layout style',
              };
              const hintText = hintMap[k];
              const showDot  = showHints && hintText && !dismissedHints.has(k);
              return (
                <div key={k} style={{ position: 'relative', flex: '1 0 30%', display: 'flex' }}>
                  <button
                    role="tab"
                    aria-selected={tab === k}
                    aria-controls={`editor-panel-${k}`}
                    id={`editor-tab-${k}`}
                    onClick={() => setTab(k)}
                    style={{
                      flex: 1, padding: '12px 8px', background: 'none', border: 'none',
                      color: tab === k ? 'var(--accent)' : 'var(--text3)',
                      fontWeight: tab === k ? 600 : 400, fontSize: 13,
                      borderBottom: tab === k ? '2px solid var(--accent)' : '2px solid transparent',
                      marginBottom: -1,
                    }}
                  >
                    {label}
                  </button>
                  {showDot && (
                    <div className="editor-hint-dot" onClick={() => setDismissedHints(p => new Set([...p, k]))}>
                      <span className="editor-hint-tooltip">{hintText}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Events tab */}
          {tab === 'events' && (
            <div
              id="editor-panel-events"
              role="tabpanel"
              aria-labelledby="editor-tab-events"
              style={{ flex: 1, overflow: 'auto', padding: 16 }}
            >
              {/* Spine reading time info */}
              {tl.orientation === 'spine' && tl.events.length > 0 && (() => {
                const totalWords = tl.events.reduce((s, ev) => {
                  const text = (ev.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                  return s + (text ? text.split(' ').length : 0);
                }, 0);
                const mins = Math.max(1, Math.ceil(totalWords / 200));
                return (
                  <div style={{ background: 'var(--accent)10', border: '1px solid var(--accent)30', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ flexShrink: 0 }}>📖</span>
                    <span>Spine layout — <strong>{tl.events.length}</strong> chapter{tl.events.length !== 1 ? 's' : ''}, ~<strong>{mins} min</strong> read. Each event becomes a full chapter with drop cap, pull quote, and hero image.</span>
                  </div>
                );
              })()}

              {/* Filmstrip photo warning */}
              {tl.orientation === 'filmstrip' && tl.events.length > 0 && canEdit && (() => {
                const noPhotoCount = tl.events.filter(e => !e.photo).length;
                if (noPhotoCount > tl.events.length / 2) return (
                  <div style={{ background: '#92400e22', border: '1px solid #92400e50', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#d97706', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ flexShrink: 0 }}>📷</span>
                    <span>The Filmstrip layout works best when most events have photos. <strong>{noPhotoCount}</strong> event{noPhotoCount !== 1 ? 's' : ''} {noPhotoCount !== 1 ? 'have' : 'has'} no photo.</span>
                  </div>
                );
                return null;
              })()}

              {minDate && (
                <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--text2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Icon name="calender" size={12} />
                    <strong style={{ color: 'var(--accent)' }}>Timeline Range</strong>
                  </div>
                  {formatDate(minDate)} → {formatDate(maxDate)}
                </div>
              )}

              {canEdit && (
                <button
                  className="btn-primary"
                  onClick={openAddEvent}
                  title={`Add Event (${isMac ? '⌘' : 'Ctrl+'}E)`}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}
                >
                  <Icon name="plus" size={16} /> Add Event
                </button>
              )}
              {canEdit && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <button
                    className="btn-secondary"
                    onClick={() => setShowCSVImport(true)}
                    disabled={importInProgress}
                    title="Import events from a CSV file"
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  >
                    <Icon name="upload" size={13} /> Import CSV
                  </button>
                  {tl.orientation === 'tree' && (
                    <button
                      className="btn-secondary"
                      onClick={() => setShowGEDCOMImport(true)}
                      disabled={importInProgress}
                      title="Import a family tree from a GEDCOM file"
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    >
                      <Icon name="users" size={13} /> GEDCOM
                    </button>
                  )}
                </div>
              )}

              {tl.events.length > 0 && (
                <div ref={exportDropRef} style={{ position: 'relative', marginBottom: 12 }}>
                  <button
                    className="btn-secondary"
                    onClick={() => setExportDropOpen(o => !o)}
                    disabled={icalExporting || carouselExporting}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  >
                    <Icon name="download" size={13} />
                    {icalExporting ? 'Exporting…' : carouselExporting ? 'Generating…' : 'Export'}
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginLeft: 'auto', flexShrink: 0, transform: exportDropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                      <path d="M2 3.5L5 6.5L8 3.5" />
                    </svg>
                  </button>

                  {exportDropOpen && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200,
                      background: 'var(--surface)', border: '1px solid var(--border2)',
                      borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)',
                      overflow: 'hidden',
                    }}>
                      {[
                        { label: 'CSV',         icon: 'download', title: 'Download events as a CSV spreadsheet',           onClick: () => { Storage.exportTimelineCSV(tl); setExportDropOpen(false); } },
                        { label: 'JSON',        icon: 'download', title: 'Download all events and settings as a JSON file', onClick: () => { handleExportJSON(); setExportDropOpen(false); } },
                        { label: 'iCal (.ics)', icon: 'calender', title: 'Download events as a calendar file',              onClick: () => { handleExportIcal(); setExportDropOpen(false); } },
                        { label: 'Carousel',    icon: 'image',    title: 'Download 1080×1080 slides for social media',      onClick: () => { handleCarouselExport(); setExportDropOpen(false); } },
                      ].map(({ label, icon, title, onClick }) => (
                        <button
                          key={label}
                          onClick={onClick}
                          title={title}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            width: '100%', padding: '9px 14px',
                            background: 'none', border: 'none',
                            color: 'var(--text2)', fontSize: 13, textAlign: 'left',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.color = 'var(--text)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text2)'; }}
                        >
                          <Icon name={icon} size={13} />
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Order controls ── */}
              {canEdit && tl.events.length > 1 && (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        role="switch"
                        aria-checked={tl.manualOrder}
                        onClick={() => update({ manualOrder: !tl.manualOrder })}
                        style={{
                          width: 36, height: 20, borderRadius: 10, padding: 0, border: 'none',
                          background: tl.manualOrder ? 'var(--accent)' : 'var(--border2)',
                          position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s',
                        }}
                      >
                        <span style={{
                          position: 'absolute', top: 3, left: tl.manualOrder ? 19 : 3,
                          width: 14, height: 14, borderRadius: '50%', background: '#fff',
                          transition: 'left 0.2s',
                        }} />
                      </button>
                      <label
                        style={{ fontSize: 12, color: 'var(--text2)', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => update({ manualOrder: !tl.manualOrder })}
                      >
                        Manual Order
                      </label>
                    </div>
                    {tl.manualOrder && (
                      <button
                        onClick={resetToChronological}
                        style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 11, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <Icon name="calender" size={11} /> Reset to date order
                      </button>
                    )}
                  </div>
                  {tl.manualOrder && (
                    <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, marginBottom: 0, lineHeight: 1.5 }}>
                      Drag events to set display order. Date-based sorting is ignored.
                    </p>
                  )}
                </div>
              )}

              {displaySorted.length === 0 && (
                <p style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 14, padding: '40px 0' }}>
                  No events yet.{canEdit && <><br />Add your first event above.</>}
                </p>
              )}

              <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={displaySorted.map(e => e.id)} strategy={verticalListSortingStrategy}>
                  <ol ref={eventListRef} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {displaySorted.map((ev, i) => (
                      <SortableEventCard
                        key={ev.id}
                        ev={ev}
                        position={i + 1}
                        manualOrder={tl.manualOrder}
                        readOnly={isViewer}
                        onEdit={() => openEditEvent(ev)}
                        onDuplicate={() => duplicateEvent(ev)}
                        onDelete={() => setDeleteConfirmId(ev.id)}
                        onFocused={setFocusedEventId}
                        showPhotoBadge={tl.orientation === 'filmstrip' || tl.orientation === 'spine'}
                        showNpBadges={tl.orientation === 'newspaper'}
                        showMapBadge={tl.orientation === 'map'}
                      />
                    ))}
                  </ol>
                </SortableContext>

                <DragOverlay dropAnimation={null}>
                  {activeEvent && (
                    <div style={{
                      background: 'var(--surface)', border: '1px solid var(--accent)',
                      borderRadius: 'var(--radius)', padding: '12px 14px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.3)', opacity: 0.95,
                    }}>
                      <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{activeEvent.title}</p>
                      <p style={{ fontSize: 11, color: 'var(--text3)' }}>
                        {formatDate(activeEvent.startDate, activeEvent.startEra !== 'NONE')}{activeEvent.endDate && activeEvent.endDate !== activeEvent.startDate ? ` → ${formatDate(activeEvent.endDate, activeEvent.endEra !== 'NONE')}` : ''}
                      </p>
                    </div>
                  )}
                </DragOverlay>
              </DndContext>
            </div>
          )}

          {/* Design tab */}
          {tab === 'design' && (
            <div
              id="editor-panel-design"
              role="tabpanel"
              aria-labelledby="editor-tab-design"
              style={{ flex: 1, overflow: 'auto', padding: 16, pointerEvents: isViewer ? 'none' : 'all', opacity: isViewer ? 0.65 : 1 }}
            >
              {/* ── Privacy — owner only ── */}
              {isOwner && (
                <div style={{ marginBottom: 24 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                    Privacy
                  </p>
                  <div style={{ display: 'flex', gap: 8, marginBottom: (tl.privacy === 'private' || tl.privacy === 'password-protected') ? 12 : 0 }}>
                    {[
                      { key: 'public',             label: 'Public',  icon: 'globe' },
                      { key: 'password-protected', label: 'Private', icon: 'lock'  },
                    ].map(({ key, label, icon }) => (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={tl.privacy === key || (key === 'password-protected' && tl.privacy === 'private')}
                        onClick={() => {
                          update({ privacy: key, ...(key === 'public' ? { password: null } : {}) });
                          if (key === 'public') setPrivacyPassword('');
                          setPrivacyError('');
                        }}
                        style={{
                          flex: 1, padding: '9px 0', borderRadius: 'var(--radius)',
                          border: `1px solid ${tl.privacy === key ? 'var(--accent)' : 'var(--border2)'}`,
                          background: tl.privacy === key ? 'var(--accent)22' : 'transparent',
                          color: tl.privacy === key ? 'var(--accent)' : 'var(--text2)',
                          cursor: 'pointer', fontSize: 13, fontWeight: tl.privacy === key ? 600 : 400,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        }}
                      >
                        <Icon name={icon} size={14} /> {label}
                      </button>
                    ))}
                  </div>
                  {(tl.privacy === 'private' || tl.privacy === 'password-protected') && (
                    <div>
                      <label style={{ fontSize: 12, marginBottom: 6 }}>
                        {tl.hasPassword ? 'Change Password' : 'Set Password'}
                        {tl.hasPassword && (
                          <span style={{ color: 'var(--text3)', fontWeight: 400 }}> — leave blank to keep current</span>
                        )}
                      </label>
                      <input
                        type="password"
                        value={privacyPassword}
                        onChange={e => { setPrivacyPassword(e.target.value); setPrivacyError(''); }}
                        placeholder={tl.hasPassword ? '••••••••' : 'Enter a password…'}
                        autoComplete="new-password"
                      />
                      {privacyError && (
                        <p role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>{privacyError}</p>
                      )}
                    </div>
                  )}
                  {tl.hasPassword && (
                    <div style={{ marginTop: 10 }}>
                      <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>
                        Share this link — visitors will be asked for the password:
                      </p>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input
                          readOnly
                          value={`${window.location.origin}/timeline/${tl.id}`}
                          style={{ flex: 1, fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', cursor: 'text' }}
                          onFocus={e => e.target.select()}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/timeline/${tl.id}`)
                              .then(() => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); })
                              .catch(() => {});
                          }}
                          style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: linkCopied ? 'var(--accent)' : 'var(--bg3)', color: linkCopied ? '#fff' : 'var(--text2)', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.15s, color 0.15s' }}
                        >
                          {linkCopied ? 'Copied!' : 'Copy link'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tl.orientation === 'filmstrip' && (
                <FilmstripSettings timeline={tl} onUpdate={update} />
              )}

              <ColorCustomizer
                colors={tl.colors || DEFAULT_COLORS}
                bgImage={tl.bgImage}
                onChange={(c) => {
                  setPreviewColors(null);
                  // Always merge — never replace. Picking a named preset theme
                  // used to spread `c` directly, which wiped out per-region
                  // colors (colors.mapRegions), the saved map style, era
                  // shading data, color-banding labels, etc.  Now we keep all
                  // existing keys and only overwrite what the preset provides.
                  update({ colors: { ...(tl.colors || DEFAULT_COLORS), ...c } });
                }}
                onBgChange={(img) => update({ bgImage: img })}
                onPreviewColor={setPreviewColors}
                onClearPreview={() => setPreviewColors(null)}
              />

              {tl.orientation === 'storyline' && (
                <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--bg3)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
                    Chapter Color Tinting
                  </p>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={!!((tl.colors || {}).chapterColorTinting)}
                      onChange={e => update({ colors: { ...(tl.colors || {}), chapterColorTinting: e.target.checked } })}
                      style={{ marginTop: 2, accentColor: 'var(--accent)', width: 'auto', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                      Apply chapter header colors as a subtle tint (~15% opacity) to event cards within each chapter. Spine dividers will also use the chapter color.
                    </span>
                  </label>
                </div>
              )}

              {tl.orientation === 'zigzag' && (
                <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--bg3)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
                    Spine Color
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="color"
                      value={(tl.colors || DEFAULT_COLORS).spineColor || (tl.colors || DEFAULT_COLORS).secondary || '#888888'}
                      onChange={e => update({ colors: { ...(tl.colors || DEFAULT_COLORS), spineColor: e.target.value } })}
                      style={{ width: 40, height: 32, padding: 2, cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'none' }}
                    />
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                      Overrides the spine, dots, and connectors color
                    </span>
                    {(tl.colors || DEFAULT_COLORS).spineColor && (
                      <button
                        type="button"
                        onClick={() => {
                          const c = { ...(tl.colors || DEFAULT_COLORS) };
                          delete c.spineColor;
                          update({ colors: c });
                        }}
                        style={{ fontSize: 11, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 'auto' }}
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Horizontal: Color Banding settings ── */}
              {tl.orientation === 'horizontal' && (() => {
                const hColors = tl.colors || DEFAULT_COLORS;
                const banding = !!hColors.colorBandingEnabled;
                const labels  = Array.isArray(hColors.bandLabels) ? hColors.bandLabels : [];
                const setLabel = (idx, value) => {
                  const next = [...labels];
                  while (next.length <= idx) next.push('');
                  next[idx] = value;
                  // Trim trailing empties
                  while (next.length > 0 && !next[next.length - 1]) next.pop();
                  update({ colors: { ...hColors, bandLabels: next } });
                };
                const PALETTE = ['#C9A96E','#6E9AC9','#6EC97A','#C96E6E','#9A6EC9','#C9B66E'];
                return (
                  <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--bg3)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
                      Color Banding
                    </p>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: banding ? 14 : 0 }}>
                      <input
                        type="checkbox"
                        checked={banding}
                        onChange={e => update({ colors: { ...hColors, colorBandingEnabled: e.target.checked } })}
                        style={{ marginTop: 2, accentColor: 'var(--accent)', width: 'auto', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                        Stack overlapping events into separate horizontal bands so they don't pile up. Bands appear only when the timeline has overlapping events.
                      </span>
                    </label>
                    {banding && (
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)', marginBottom: 8 }}>
                          Band labels (optional — used in the on-timeline legend)
                        </p>
                        {PALETTE.map((color, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span aria-hidden="true" style={{ display: 'inline-block', width: 14, height: 14, background: `${color}66`, border: `1px solid ${color}`, borderRadius: 3, flexShrink: 0 }} />
                            <input
                              type="text"
                              value={labels[i] || ''}
                              onChange={e => setLabel(i, e.target.value)}
                              placeholder={`Band ${i + 1}`}
                              maxLength={40}
                              style={{ flex: 1, fontSize: 12, padding: '4px 8px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text)' }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Horizontal: Era Shading settings ── */}
              {tl.orientation === 'horizontal' && (() => {
                const hColors = tl.colors || DEFAULT_COLORS;
                const eras = Array.isArray(hColors.eras) ? hColors.eras : [];
                const ERA_PALETTE = ['#C9A96E','#6E9AC9','#6EC97A','#C96E6E','#9A6EC9','#C9B66E'];
                // writeEras updates local state AND schedules a debounced
                // auto-save so design-tab changes persist without the user
                // having to remember to click Save.
                const writeEras = (next) => {
                  const updatedTl = { ...tl, colors: { ...hColors, eras: next } };
                  setTl(updatedTl);
                  setIsDirty(true);
                  if (designSaveTimerRef.current) clearTimeout(designSaveTimerRef.current);
                  designSaveTimerRef.current = setTimeout(() => autoSave(updatedTl), 700);
                };
                const addEra = () => {
                  if (eras.length >= 10) return;
                  const nextColor = ERA_PALETTE[eras.length % ERA_PALETTE.length];
                  writeEras([...eras, { label: '', startDate: '', endDate: '', color: nextColor }]);
                };
                const setEraField = (idx, field, value) => {
                  const next = eras.map((e, i) => i === idx ? { ...e, [field]: value } : e);
                  writeEras(next);
                };
                const removeEra = (idx) => writeEras(eras.filter((_, i) => i !== idx));
                const generateFromChapters = () => {
                  // Chapters may live in tl.chapters AND/OR on individual events as
                  // the `chapter` field — collect both, preserving tl.chapters order
                  // first then appending any event-derived names not already present.
                  const seen = new Set();
                  const chapterNames = [];
                  (tl.chapters || []).forEach(c => {
                    const name = (typeof c === 'string' ? c : c?.name || '').trim();
                    if (name && !seen.has(name)) { chapterNames.push(name); seen.add(name); }
                  });
                  (tl.events || []).forEach(e => {
                    const name = (e.chapter || '').trim();
                    if (name && !seen.has(name)) { chapterNames.push(name); seen.add(name); }
                  });
                  if (chapterNames.length === 0) {
                    alert('No chapters to generate from. Add chapters first in the Events tab.');
                    return;
                  }
                  if (eras.length > 0 && !window.confirm('This will replace your existing eras. Continue?')) return;
                  const newEras = chapterNames.map((name, i) => {
                    const inChapter = (tl.events || []).filter(ev => ev.chapter === name);
                    const starts = inChapter.map(e => e.startDate).filter(Boolean).sort();
                    const ends   = inChapter.map(e => e.endDate || e.startDate).filter(Boolean).sort();
                    return {
                      label: name,
                      startDate: starts[0] || '',
                      endDate:   ends[ends.length - 1] || '',
                      color:     ERA_PALETTE[i % ERA_PALETTE.length],
                    };
                  }).filter(e => e.startDate && e.endDate);
                  if (newEras.length === 0) {
                    alert('Chapters exist but contain no events with valid dates.');
                    return;
                  }
                  writeEras(newEras);
                };
                return (
                  <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--bg3)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
                        Eras
                      </p>
                      <button
                        type="button"
                        onClick={generateFromChapters}
                        style={{ fontSize: 11, fontWeight: 500, padding: '4px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text2)', cursor: 'pointer' }}
                      >
                        Generate from Chapters
                      </button>
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10, lineHeight: 1.5 }}>
                      Eras render as full-height shaded bands behind the timeline. Up to 10.
                    </p>
                    {eras.map((era, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr auto', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                        <input
                          type="color"
                          value={era.color || ERA_PALETTE[i % ERA_PALETTE.length]}
                          onChange={e => setEraField(i, 'color', e.target.value)}
                          aria-label={`Era ${i + 1} color`}
                          style={{ width: 32, height: 28, padding: 1, cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'none' }}
                        />
                        <input
                          type="text"
                          value={era.label || ''}
                          onChange={e => setEraField(i, 'label', e.target.value)}
                          placeholder="Label"
                          maxLength={40}
                          style={{ fontSize: 12, padding: '4px 8px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text)' }}
                        />
                        <input
                          type="text"
                          value={era.startDate || ''}
                          onChange={e => setEraField(i, 'startDate', e.target.value)}
                          placeholder="Start YYYY-MM-DD"
                          style={{ fontSize: 12, padding: '4px 8px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}
                        />
                        <input
                          type="text"
                          value={era.endDate || ''}
                          onChange={e => setEraField(i, 'endDate', e.target.value)}
                          placeholder="End YYYY-MM-DD"
                          style={{ fontSize: 12, padding: '4px 8px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}
                        />
                        <button
                          type="button"
                          onClick={() => removeEra(i)}
                          aria-label="Remove era"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: '2px 6px', fontSize: 16 }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {eras.length < 10 && (
                      <button
                        type="button"
                        onClick={addEra}
                        style={{ marginTop: 8, fontSize: 12, fontWeight: 500, padding: '6px 12px', background: 'var(--bg2)', border: '1px dashed var(--border)', borderRadius: 'var(--radius)', color: 'var(--text2)', cursor: 'pointer', width: '100%' }}
                      >
                        + Add Era
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* ── Map: Style picker ── */}
              {tl.orientation === 'map' && (() => {
                const mColors = tl.colors || DEFAULT_COLORS;
                const currentStyle = mColors.mapStyle || 'standard';
                const STYLES = [
                  { id: 'standard',   label: 'Standard',   hint: 'OpenStreetMap' },
                  { id: 'minimal',    label: 'Minimal',    hint: 'CartoDB Positron' },
                  { id: 'dark',       label: 'Dark',       hint: 'CartoDB Dark Matter' },
                  { id: 'terrain',    label: 'Terrain',    hint: 'OpenTopoMap' },
                  { id: 'watercolor', label: 'Watercolor', hint: 'Stadia / Stamen' },
                  { id: 'antique',    label: 'Antique',    hint: 'Stadia / Stamen Toner' },
                ];
                const setStyle = (id) => {
                  const updatedTl = { ...tl, colors: { ...mColors, mapStyle: id } };
                  setTl(updatedTl);
                  setIsDirty(true);
                  if (designSaveTimerRef.current) clearTimeout(designSaveTimerRef.current);
                  designSaveTimerRef.current = setTimeout(() => autoSave(updatedTl), 700);
                };
                return (
                  <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--bg3)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                      Map Style
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                      {STYLES.map(s => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setStyle(s.id)}
                          style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                            padding: '8px 10px',
                            background: currentStyle === s.id ? 'var(--accent)' : 'var(--bg2)',
                            color: currentStyle === s.id ? '#1A1408' : 'var(--text)',
                            border: `1px solid ${currentStyle === s.id ? 'var(--accent)' : 'var(--border)'}`,
                            borderRadius: 'var(--radius)',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: currentStyle === s.id ? 700 : 500,
                            textAlign: 'left',
                          }}
                        >
                          <span>{s.label}</span>
                          <span style={{ fontSize: 10, opacity: currentStyle === s.id ? 0.85 : 0.6, fontWeight: 400 }}>{s.hint}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ── Map: Legend settings ── */}
              {tl.orientation === 'map' && (() => {
                const mColors = tl.colors || DEFAULT_COLORS;
                const enabled = !!mColors.showMapLegend;
                const iconLabels = mColors.mapIconLabels || {};
                const ls = mColors.mapLegendSettings || {};
                const customEntries = Array.isArray(ls.customEntries) ? ls.customEntries : [];
                // Detect which custom icons are actually used on this timeline
                const usedIcons = [];
                const seen = new Set();
                (tl.events || []).forEach(ev => {
                  const k = ev.mapIcon;
                  if (k && k !== 'pin' && !seen.has(k)) { seen.add(k); usedIcons.push(k); }
                });
                const ICON_EMOJI = { sword: '⚔️', crown: '👑', column: '🏛️', skull: '💀', flame: '🔥', ship: '🚢', book: '📖', coin: '🪙', star: '⭐', flag: '🚩', person: '👤' };
                const ICON_NAMES = { sword: 'Sword', crown: 'Crown', column: 'Column', skull: 'Skull', flame: 'Flame', ship: 'Ship', book: 'Book', coin: 'Coin', star: 'Star', flag: 'Flag', person: 'Person' };
                const CUSTOM_ICON_OPTIONS = ['', '⭐', '🚩', '⚔️', '👑', '🏛️', '💀', '🔥', '🚢', '📖', '🪙', '👤', '🌋', '⛰️', '🏰', '⚓', '🛡️', '📍'];
                const writeColors = (patch) => {
                  const updatedTl = { ...tl, colors: { ...mColors, ...patch } };
                  setTl(updatedTl);
                  setIsDirty(true);
                  if (designSaveTimerRef.current) clearTimeout(designSaveTimerRef.current);
                  designSaveTimerRef.current = setTimeout(() => autoSave(updatedTl), 700);
                };
                const setEnabled    = (val) => writeColors({ showMapLegend: val });
                const setIconLabel  = (key, value) => {
                  const next = { ...iconLabels, [key]: value };
                  if (!value) delete next[key];
                  writeColors({ mapIconLabels: next });
                };
                const setLegend = (patch) => writeColors({ mapLegendSettings: { ...ls, ...patch } });
                const setEntries = (entries) => setLegend({ customEntries: entries });

                const POSITIONS = [
                  { id: 'tl', label: 'Top Left'     },
                  { id: 'tr', label: 'Top Right'    },
                  { id: 'bl', label: 'Bottom Left'  },
                  { id: 'br', label: 'Bottom Right' },
                ];
                const FONT_SIZES = [
                  { id: 'small',  label: 'S' },
                  { id: 'medium', label: 'M' },
                  { id: 'large',  label: 'L' },
                ];
                const inputStyle = { fontSize: 12, padding: '4px 8px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text)' };
                const rowStyle   = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 };
                const labelStyle = { fontSize: 11, color: 'var(--text2)', width: 110, flexShrink: 0 };
                const segBtn = (active) => ({
                  padding: '4px 10px', fontSize: 11,
                  background: active ? 'var(--accent)' : 'var(--bg2)',
                  color: active ? '#1A1408' : 'var(--text2)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  cursor: 'pointer',
                });

                return (
                  <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--bg3)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
                      Map Legend
                    </p>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: enabled ? 14 : 0 }}>
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={e => setEnabled(e.target.checked)}
                        style={{ marginTop: 2, accentColor: 'var(--accent)', width: 'auto', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>
                        Show a legend on the map. Lists every region with a label, each custom marker icon in use, and any standalone labels you've placed.
                      </span>
                    </label>
                    {enabled && (
                      <div style={{ marginTop: 12 }}>
                        <div style={rowStyle}>
                          <span style={labelStyle}>Title</span>
                          <input
                            type="text"
                            value={ls.title || ''}
                            onChange={e => setLegend({ title: e.target.value })}
                            placeholder="Legend"
                            maxLength={40}
                            style={{ ...inputStyle, flex: 1 }}
                          />
                        </div>
                        <div style={rowStyle}>
                          <span style={labelStyle}>Position</span>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, flex: 1 }}>
                            {POSITIONS.map(p => {
                              const active = (ls.position || 'tl') === p.id;
                              return (
                                <button key={p.id} type="button" onClick={() => setLegend({ position: p.id })}
                                  style={segBtn(active)} aria-pressed={active}>
                                  {p.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div style={rowStyle}>
                          <span style={labelStyle}>Font size</span>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {FONT_SIZES.map(s => {
                              const active = (ls.fontSize || 'medium') === s.id;
                              return (
                                <button key={s.id} type="button" onClick={() => setLegend({ fontSize: s.id })}
                                  style={{ ...segBtn(active), minWidth: 32 }} aria-pressed={active}>
                                  {s.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div style={rowStyle}>
                          <span style={labelStyle}>Text color</span>
                          <input type="color" value={ls.textColor || '#F0E6CC'}
                            onChange={e => setLegend({ textColor: e.target.value })}
                            style={{ width: 36, height: 28, border: 'none', background: 'transparent', cursor: 'pointer' }}
                            aria-label="Text color" />
                          <span style={labelStyle}>Border color</span>
                          <input type="color" value={ls.borderColor || '#C9A96E'}
                            onChange={e => setLegend({ borderColor: e.target.value })}
                            style={{ width: 36, height: 28, border: 'none', background: 'transparent', cursor: 'pointer' }}
                            aria-label="Border color" />
                        </div>
                        <div style={rowStyle}>
                          <span style={labelStyle}>Background</span>
                          <input type="color" value={ls.backgroundColor || '#1A1408'}
                            onChange={e => setLegend({ backgroundColor: e.target.value })}
                            style={{ width: 36, height: 28, border: 'none', background: 'transparent', cursor: 'pointer' }}
                            aria-label="Background color" />
                          <input type="range" min={0} max={1} step={0.05}
                            value={typeof ls.backgroundOpacity === 'number' ? ls.backgroundOpacity : 0.9}
                            onChange={e => setLegend({ backgroundOpacity: parseFloat(e.target.value) })}
                            style={{ flex: 1 }}
                            aria-label="Background opacity" />
                          <span style={{ fontSize: 11, color: 'var(--text3)', width: 40, textAlign: 'right' }}>
                            {Math.round((typeof ls.backgroundOpacity === 'number' ? ls.backgroundOpacity : 0.9) * 100)}%
                          </span>
                        </div>
                        <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                          <p style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)', marginBottom: 8 }}>
                            Show entries
                          </p>
                          {[
                            { key: 'showRegionEntries', label: 'Regions' },
                            { key: 'showMarkerEntries', label: 'Marker icons' },
                            { key: 'showLabelEntries',  label: 'Labels'      },
                          ].map(t => (
                            <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 12, color: 'var(--text2)' }}>
                              <input type="checkbox"
                                checked={ls[t.key] !== false}
                                onChange={e => setLegend({ [t.key]: e.target.checked })}
                                style={{ accentColor: 'var(--accent)', width: 'auto' }} />
                              {t.label}
                            </label>
                          ))}
                        </div>
                        {usedIcons.length > 0 && (
                          <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                            <p style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)', marginBottom: 8 }}>
                              Legend labels for marker icons
                            </p>
                            {usedIcons.map(k => (
                              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <span style={{ width: 24, textAlign: 'center', fontSize: 16 }} aria-hidden="true">{ICON_EMOJI[k] || '•'}</span>
                                <input
                                  type="text"
                                  value={iconLabels[k] || ''}
                                  onChange={e => setIconLabel(k, e.target.value)}
                                  placeholder={ICON_NAMES[k] || k}
                                  maxLength={40}
                                  style={{ ...inputStyle, flex: 1 }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                          <p style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)', marginBottom: 8 }}>
                            Custom entries (up to 10)
                          </p>
                          {customEntries.map((entry, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                              <input type="color" value={entry.color || '#C9A96E'}
                                onChange={e => {
                                  const next = customEntries.map((x, idx) => idx === i ? { ...x, color: e.target.value } : x);
                                  setEntries(next);
                                }}
                                style={{ width: 28, height: 26, border: 'none', background: 'transparent', cursor: 'pointer', flexShrink: 0 }}
                                aria-label="Entry color" />
                              <select
                                value={entry.icon || ''}
                                onChange={e => {
                                  const next = customEntries.map((x, idx) => idx === i ? { ...x, icon: e.target.value } : x);
                                  setEntries(next);
                                }}
                                style={{ ...inputStyle, width: 50, flexShrink: 0 }}
                                aria-label="Entry icon"
                              >
                                {CUSTOM_ICON_OPTIONS.map(opt => (
                                  <option key={opt || 'none'} value={opt}>{opt || '—'}</option>
                                ))}
                              </select>
                              <input
                                type="text"
                                value={entry.text || ''}
                                onChange={e => {
                                  const next = customEntries.map((x, idx) => idx === i ? { ...x, text: e.target.value } : x);
                                  setEntries(next);
                                }}
                                placeholder="Label text"
                                maxLength={40}
                                style={{ ...inputStyle, flex: 1 }}
                              />
                              <button type="button"
                                onClick={() => setEntries(customEntries.filter((_, idx) => idx !== i))}
                                style={{ background: 'transparent', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}
                                aria-label="Remove entry"
                                title="Remove entry"
                              >×</button>
                            </div>
                          ))}
                          {customEntries.length < 10 && (
                            <button type="button"
                              onClick={() => setEntries([...customEntries, { color: '#C9A96E', icon: '', text: '' }])}
                              style={{ ...segBtn(false), marginTop: 4 }}
                            >+ Add entry</button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Map: Region Highlighting settings ── */}
              {tl.orientation === 'map' && (() => {
                const mColors = tl.colors || DEFAULT_COLORS;
                const mapRegions = Array.isArray(mColors.mapRegions) ? mColors.mapRegions : [];
                const writeRegions = (next) => {
                  const updatedTl = { ...tl, colors: { ...mColors, mapRegions: next } };
                  setTl(updatedTl);
                  setIsDirty(true);
                  if (designSaveTimerRef.current) clearTimeout(designSaveTimerRef.current);
                  designSaveTimerRef.current = setTimeout(() => autoSave(updatedTl), 700);
                };
                // Route deletes through MapTimeline so they go through
                // persistRegions + the property-level undo history.
                const removeRegion = (id) => {
                  window.dispatchEvent(new CustomEvent('chronos:mapDeleteRegion', { detail: { id } }));
                };
                return (
                  <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--bg3)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
                        Regions
                      </p>
                      <button
                        type="button"
                        onClick={() => window.dispatchEvent(new CustomEvent('chronos:mapDrawRegion'))}
                        style={{ fontSize: 11, fontWeight: 500, padding: '4px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text2)', cursor: 'pointer' }}
                      >
                        + Draw New Region
                      </button>
                    </div>
                    {mapRegions.length === 0 ? (
                      <p style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic', lineHeight: 1.5, margin: 0 }}>
                        No regions yet. Draw directly on the map to highlight an area.
                      </p>
                    ) : (
                      mapRegions.map(region => (
                        <div key={region.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, padding: '6px 8px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                          <span aria-hidden="true" style={{ display: 'inline-block', width: 14, height: 14, background: `${region.color}${Math.round((region.opacity || 0.25) * 255).toString(16).padStart(2, '0')}`, border: `1px solid ${region.color}`, borderRadius: 3, flexShrink: 0 }} />
                          <span style={{ flex: 1, fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {region.label || <span style={{ color: 'var(--text3)', fontStyle: 'italic' }}>(no label)</span>}
                          </span>
                          <button
                            type="button"
                            onClick={() => window.dispatchEvent(new CustomEvent('chronos:mapEditRegion', { detail: { id: region.id } }))}
                            title="Edit region"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: '2px 6px', fontSize: 12 }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => removeRegion(region.id)}
                            aria-label="Delete region"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '2px 6px', fontSize: 14 }}
                          >
                            ✕
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                );
              })()}

              {/* ── Newspaper Design settings ── */}
              {tl.orientation === 'newspaper' && (() => {
                const npColors = tl.colors || DEFAULT_COLORS;
                const npUpdate = (patch) => update({ colors: { ...npColors, ...patch } });
                const colCount = npColors.npColumnCount || 3;
                return (
                  <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--bg3)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
                      Newspaper Settings
                    </p>

                    {/* Column count thumbnails */}
                    <div style={{ marginBottom: 16 }}>
                      <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8, fontWeight: 500 }}>Column Count</p>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[2, 3, 4].map(n => (
                          <button
                            key={n}
                            type="button"
                            aria-pressed={colCount === n}
                            onClick={() => npUpdate({ npColumnCount: n })}
                            style={{
                              flex: 1, padding: '10px 0', borderRadius: 'var(--radius)',
                              border: `1px solid ${colCount === n ? 'var(--accent)' : 'var(--border2)'}`,
                              background: colCount === n ? 'var(--accent)22' : 'transparent',
                              color: colCount === n ? 'var(--accent)' : 'var(--text2)',
                              cursor: 'pointer', display: 'flex', flexDirection: 'column',
                              alignItems: 'center', gap: 5,
                            }}
                          >
                            <div className="np-col-thumb" style={{ display: 'flex', gap: 2, height: 24, alignItems: 'stretch' }}>
                              {Array.from({ length: n }).map((_, i) => (
                                <div key={i} style={{
                                  flex: 1, background: colCount === n ? 'var(--accent)' : 'var(--border2)',
                                  borderRadius: 2, minWidth: 6,
                                }} />
                              ))}
                            </div>
                            <span style={{ fontSize: 12, fontWeight: colCount === n ? 600 : 400 }}>{n} cols</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Edition Header */}
                    <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: npColors.npEditionHeader ? 10 : 0 }}>
                        <input
                          type="checkbox"
                          checked={!!npColors.npEditionHeader}
                          onChange={e => npUpdate({ npEditionHeader: e.target.checked })}
                          style={{ accentColor: 'var(--accent)', width: 'auto', flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>Edition Header / Masthead</span>
                      </label>
                      {npColors.npEditionHeader && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginLeft: 24 }}>
                          <div>
                            <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Tagline</label>
                            <input
                              value={npColors.npTagline || ''}
                              onChange={e => npUpdate({ npTagline: e.target.value })}
                              placeholder="e.g. All the news fit to print"
                              style={{ fontSize: 12 }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Edition / Issue Number</label>
                            <input
                              value={npColors.npEditionNumber || ''}
                              onChange={e => npUpdate({ npEditionNumber: e.target.value })}
                              placeholder="e.g. Vol. 1, No. 42"
                              style={{ fontSize: 12 }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Breaking News Ticker */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
                      <input
                        type="checkbox"
                        checked={!!npColors.npBreakingNewsTicker}
                        onChange={e => npUpdate({ npBreakingNewsTicker: e.target.checked })}
                        style={{ accentColor: 'var(--accent)', width: 'auto', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>Breaking News Ticker</span>
                    </label>

                    {/* Pull Quotes */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
                      <input
                        type="checkbox"
                        checked={npColors.npPullQuotes !== false}
                        onChange={e => npUpdate({ npPullQuotes: e.target.checked })}
                        style={{ accentColor: 'var(--accent)', width: 'auto', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>Pull Quotes</span>
                    </label>

                    {/* Sepia Mode */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
                      <input
                        type="checkbox"
                        checked={!!npColors.npSepia}
                        onChange={e => npUpdate({ npSepia: e.target.checked })}
                        style={{ accentColor: 'var(--accent)', width: 'auto', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>Sepia / Parchment Mode</span>
                    </label>

                    {/* Scan Lines */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
                      <input
                        type="checkbox"
                        checked={!!npColors.npScanLines}
                        onChange={e => npUpdate({ npScanLines: e.target.checked })}
                        style={{ accentColor: 'var(--accent)', width: 'auto', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>Scan Lines Overlay</span>
                    </label>

                    {/* Grayscale Default */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0 }}>
                      <input
                        type="checkbox"
                        checked={!!npColors.npGrayscaleDefault}
                        onChange={e => npUpdate({ npGrayscaleDefault: e.target.checked })}
                        style={{ accentColor: 'var(--accent)', width: 'auto', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>Open in Grayscale by Default</span>
                    </label>
                  </div>
                );
              })()}
            </div>
          )}

        {/* History tab */}
        {tab === 'history' && (
          <div
            id="editor-panel-history"
            role="tabpanel"
            aria-labelledby="editor-tab-history"
            style={{ flex: 1, overflow: 'auto', padding: 24, minHeight: 0 }}
          >
            <button type="button" onClick={() => setTab('events')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '6px 14px', color: 'var(--text2)', fontSize: 13, cursor: 'pointer', marginBottom: 24 }}>
              <Icon name="arrowLeft" size={13} />Back to editing
            </button>
            <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 16 }}>
              A snapshot is saved automatically each time you click Save. The 20 most recent versions are kept.
            </p>

            {!tl.id ? (
              <p style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '40px 0' }}>
                Save this timeline once to start tracking version history.
              </p>
            ) : versionsLoading ? (
              <p style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '40px 0' }}>
                Loading…
              </p>
            ) : versions.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '40px 0' }}>
                No saved versions yet. Click Save to create the first snapshot.
              </p>
            ) : (
              <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {versions.map((ver, i) => (
                  <li
                    key={ver.id}
                    style={{
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 10,
                    }}
                  >
                    {/* Top row */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                      <span style={{
                        flexShrink: 0,
                        background: i === 0 ? 'var(--accent)22' : 'var(--bg3)',
                        color:      i === 0 ? 'var(--accent)'   : 'var(--text3)',
                        border:    `1px solid ${i === 0 ? 'var(--accent)60' : 'var(--border)'}`,
                        borderRadius: 6, padding: '2px 7px', fontSize: 11, fontWeight: 700,
                      }}>
                        v{ver.versionNumber}
                        {i === 0 && <span style={{ marginLeft: 5, fontWeight: 400, fontSize: 10 }}>latest</span>}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                          fontSize: 12, color: 'var(--text)', fontWeight: 500,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          marginBottom: 2,
                        }}>
                          {ver.title}
                        </p>
                        <p style={{ fontSize: 11, color: 'var(--text3)' }}>
                          {ver.eventCount} event{ver.eventCount !== 1 ? 's' : ''} ·{' '}
                          {new Date(ver.savedAt).toLocaleString(undefined, {
                            month: 'short', day: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <button
                        className="btn-ghost btn-sm"
                        onClick={() => openVersionPreview(ver)}
                        style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <Icon name="eye" size={12} /> Preview
                      </button>
                    </div>

                    {/* Label row */}
                    {editingLabelId === ver.id ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          autoFocus
                          value={inlineLabelText}
                          onChange={e => setInlineLabelText(e.target.value.slice(0, 100))}
                          placeholder="Label this version…"
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSaveLabel(ver.id, inlineLabelText);
                            if (e.key === 'Escape') setEditingLabelId(null);
                          }}
                          style={{ flex: 1, fontSize: 12, padding: '4px 8px' }}
                        />
                        <button className="btn-primary btn-sm" onClick={() => handleSaveLabel(ver.id, inlineLabelText)}>Save</button>
                        <button className="btn-ghost btn-sm" onClick={() => setEditingLabelId(null)}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button
                          onClick={() => { setEditingLabelId(ver.id); setInlineLabelText(ver.label || ''); }}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
                            color: ver.label ? 'var(--text2)' : 'var(--text3)',
                          }}
                        >
                          <Icon name="pencil" size={11} />
                          {ver.label
                            ? ver.label
                            : <span style={{ fontStyle: 'italic' }}>Add label…</span>}
                        </button>
                        <div style={{ flex: 1 }} />
                        {canEdit && (
                          <button
                            className="btn-ghost btn-sm"
                            onClick={() => setRestoreTarget(ver)}
                            style={{ color: 'var(--accent)', borderColor: 'var(--accent)40', display: 'flex', alignItems: 'center', gap: 4 }}
                          >
                            <Icon name="history" size={12} /> Restore
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        {/* Chapters tab — storyline layout only */}
        {tab === 'chapters' && tl.orientation === 'storyline' && canEdit && (() => {
          const normChapters = getNormChapters();
          // DnD must use the same array that handleChapterDragEnd operates on.
          // getChapterOrder() appends event-derived chapters not in tl.chapters,
          // which causes indexOf to return -1 and silently abort the reorder.
          const dndNames = normChapters.map(c => c.name);
          const uncategorizedCount = tl.events.filter(e => !e.chapter).length;
          return (
            <div
              id="editor-panel-chapters"
              role="tabpanel"
              aria-labelledby="editor-tab-chapters"
              style={{ flex: 1, overflow: 'auto', padding: 16 }}
            >
              <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 16 }}>
                Organize events into chapters. Drag to reorder. Click the emoji or color swatch to customize each chapter.
              </p>

              {/* Add chapter */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input
                  value={newChapterName}
                  onChange={e => setNewChapterName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addChapter(); }}
                  placeholder="New chapter name…"
                  style={{ flex: 1 }}
                  aria-label="New chapter name"
                />
                <button
                  className="btn-primary btn-sm"
                  onClick={addChapter}
                  disabled={!newChapterName.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
                >
                  <Icon name="plus" size={13} /> Add
                </button>
              </div>

              {dndNames.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '40px 0', lineHeight: 1.6 }}>
                  No chapters yet.<br />Add one above or assign a chapter when editing an event.
                </p>
              ) : (
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={closestCenter}
                  onDragStart={({ active }) => setActiveChapterId(active.id)}
                  onDragEnd={handleChapterDragEnd}
                >
                  <SortableContext items={dndNames} strategy={verticalListSortingStrategy}>
                    <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {normChapters.map(chObj => {
                        const ch = chObj.name;
                        return (
                          <SortableChapterRow
                            key={ch}
                            chapter={chObj}
                            eventCount={tl.events.filter(e => e.chapter === ch).length}
                            allEvents={displaySorted}
                            isRenaming={renamingChapter === ch}
                            renameValue={renameValue}
                            onRenameStart={(name) => { setRenamingChapter(name); setRenameValue(name); }}
                            onRenameChange={setRenameValue}
                            onRenameCommit={(val) => renameChapter(ch, val)}
                            onRenameCancel={() => setRenamingChapter(null)}
                            onDelete={deleteChapter}
                            isAssigning={chapterAssignTarget === ch}
                            onToggleAssign={() => setChapterAssignTarget(prev => prev === ch ? null : ch)}
                            onEventAssign={(eventId, chapter) => update({ events: tl.events.map(e => e.id === eventId ? { ...e, chapter } : e) })}
                            onMetaUpdate={(patch) => updateChapterMeta(ch, patch)}
                          />
                        );
                      })}
                    </ol>
                  </SortableContext>
                  <DragOverlay dropAnimation={null}>
                    {activeChapterId && (
                      <div style={{ background: 'var(--surface)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', padding: '10px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{activeChapterId}</span>
                      </div>
                    )}
                  </DragOverlay>
                </DndContext>
              )}

              {uncategorizedCount > 0 && (
                <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 16, padding: '10px 12px', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', lineHeight: 1.5 }}>
                  {uncategorizedCount} event{uncategorizedCount !== 1 ? 's' : ''} without a chapter will appear under "Uncategorized".
                </p>
              )}
            </div>
          );
        })()}

        {/* Swimlanes tab — swimlane layout only */}
        {tab === 'swimlanes' && tl.orientation === 'swimlane' && canEdit && (() => {
          const swimlaneOrder = getSwimlaneOrder();
          const unassignedCount = tl.events.filter(e => !e.swimlane).length;
          return (
            <div
              id="editor-panel-swimlanes"
              role="tabpanel"
              aria-labelledby="editor-tab-swimlanes"
              style={{ flex: 1, overflow: 'auto', padding: 16 }}
            >
              <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 16 }}>
                Organize events into swimlanes. Drag to reorder. Click the color swatch to change lane color.
              </p>

              {/* Add swimlane */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input
                  value={newSwimlaneName}
                  onChange={e => setNewSwimlaneName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addSwimlane(); }}
                  placeholder="New swimlane name…"
                  style={{ flex: 1 }}
                  aria-label="New swimlane name"
                />
                <button
                  className="btn-primary btn-sm"
                  onClick={addSwimlane}
                  disabled={!newSwimlaneName.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
                >
                  <Icon name="plus" size={13} /> Add
                </button>
              </div>

              {swimlaneOrder.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '40px 0', lineHeight: 1.6 }}>
                  No swimlanes yet.<br />Add one above or assign a swimlane when editing an event.
                </p>
              ) : (
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={closestCenter}
                  onDragStart={({ active }) => setActiveSwimlaneId(active.id)}
                  onDragEnd={handleSwimlaneDragEnd}
                >
                  <SortableContext items={swimlaneOrder} strategy={verticalListSortingStrategy}>
                    <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {swimlaneOrder.map(sl => (
                        <SortableSwimlaneRow
                          key={sl}
                          name={sl}
                          color={(tl.swimlaneColors || {})[sl] || null}
                          eventCount={tl.events.filter(e => e.swimlane === sl).length}
                          allEvents={displaySorted}
                          isRenaming={renamingSwimlane === sl}
                          renameValue={renameSwimlaneValue}
                          onRenameStart={(name) => { setRenamingSwimlane(name); setRenameSwimlaneValue(name); }}
                          onRenameChange={setRenameSwimlaneValue}
                          onRenameCommit={(val) => renameSwimlane(sl, val)}
                          onRenameCancel={() => setRenamingSwimlane(null)}
                          onDelete={deleteSwimlane}
                          onColorChange={(color) => setSwimlaneColor(sl, color)}
                          isAssigning={swimlaneAssignTarget === sl}
                          onToggleAssign={() => setSwimlaneAssignTarget(prev => prev === sl ? null : sl)}
                          onEventAssign={(eventId, swimlane) => update({ events: tl.events.map(e => e.id === eventId ? { ...e, swimlane } : e) })}
                        />
                      ))}
                    </ol>
                  </SortableContext>
                  <DragOverlay dropAnimation={null}>
                    {activeSwimlaneId && (
                      <div style={{ background: 'var(--surface)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', padding: '10px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{activeSwimlaneId}</span>
                      </div>
                    )}
                  </DragOverlay>
                </DndContext>
              )}

              {unassignedCount > 0 && (
                <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 16, padding: '10px 12px', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', lineHeight: 1.5 }}>
                  {unassignedCount} event{unassignedCount !== 1 ? 's' : ''} without a swimlane will appear in an "Unassigned" track.
                </p>
              )}
            </div>
          );
        })()}

        {/* Tree tab */}
        {tab === 'tree' && canEdit && (() => {
          const { outline, nm: treeNm } = getTreeOutline();
          const hasAnyParent = tl.events.some(e => e.parentEventId);
          return (
            <div
              id="editor-panel-tree"
              role="tabpanel"
              aria-labelledby="editor-tab-tree"
              style={{ flex: 1, overflow: 'auto', padding: 16 }}
            >
              <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 12 }}>
                Build and manage your tree structure. Click ▶ to expand branches in this outline.
              </p>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <button
                  className="btn-ghost btn-sm"
                  onClick={handleAutoDetectBranches}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  title="Groups events by swimlane or chapter into linear branches"
                >
                  <Icon name="feed" size={13} /> Auto-Detect Branches
                </button>
                {hasAnyParent && (
                  <button
                    className="btn-ghost btn-sm"
                    onClick={() => update({ events: tl.events.map(e => ({ ...e, parentEventId: null })) })}
                    style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Icon name="trash" size={13} /> Clear All Parents
                  </button>
                )}
              </div>

              {/* Tree outline */}
              {outline.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '40px 0', lineHeight: 1.6 }}>
                  No events yet.
                </p>
              ) : (
                <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {outline.map(({ id, ev, depth, hasChildren }) => (
                    <li
                      key={id}
                      style={{
                        paddingLeft: depth * 18 + 8,
                        paddingRight: 8,
                        paddingTop: 6,
                        paddingBottom: 6,
                        borderBottom: '1px solid var(--border)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* Expand toggle */}
                      <button
                        type="button"
                        onClick={() => setTreeExpandedIds(s => {
                          const n = new Set(s);
                          n.has(id) ? n.delete(id) : n.add(id);
                          return n;
                        })}
                        style={{
                          background: 'none', border: 'none', cursor: hasChildren ? 'pointer' : 'default',
                          color: hasChildren ? 'var(--accent)' : 'var(--border)', fontSize: 11, padding: '0 2px', flexShrink: 0,
                        }}
                        aria-label={treeExpandedIds.has(id) ? 'Collapse' : 'Expand'}
                      >
                        {hasChildren ? (treeExpandedIds.has(id) ? '▼' : '▶') : '·'}
                      </button>

                      {/* Branch color swatch */}
                      <label
                        title="Branch color"
                        style={{ position: 'relative', width: 14, height: 14, borderRadius: 3, background: ev.branchColor || 'var(--border2)', cursor: 'pointer', flexShrink: 0, border: '1px solid var(--border)' }}
                      >
                        <input
                          type="color"
                          value={ev.branchColor || '#4A8FD4'}
                          onChange={e => update({ events: tl.events.map(ev2 => ev2.id === id ? { ...ev2, branchColor: e.target.value } : ev2) })}
                          style={{ opacity: 0, position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'pointer', border: 'none', padding: 0 }}
                        />
                      </label>

                      {/* Title */}
                      <span style={{ fontSize: 12, fontWeight: depth === 0 ? 700 : 500, color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ev.title}
                      </span>

                      {/* Parent selector */}
                      <select
                        value={ev.parentEventId || ''}
                        onChange={e => setParentEvent(id, e.target.value || null)}
                        style={{ fontSize: 10, padding: '2px 4px', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)', maxWidth: 100 }}
                        title="Set parent event"
                      >
                        <option value="">— Root —</option>
                        {tl.events
                          .filter(other => other.id !== id && !getTreeDescendants(id, tl.events).has(other.id))
                          .sort((a, b) => a.title.localeCompare(b.title))
                          .map(other => (
                            <option key={other.id} value={other.id}>{other.title.slice(0, 22)}</option>
                          ))}
                      </select>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          );
        })()}

        {/* Collaborators tab — owner only */}
        {tab === 'collaborators' && isOwner && (
          <div
            id="editor-panel-collaborators"
            role="tabpanel"
            aria-labelledby="editor-tab-collaborators"
            style={{ flex: 1, overflow: 'auto', padding: 32 }}
          >
            <div style={{ maxWidth: 640, margin: '0 auto' }}>
            {/* ── Back button ── */}
            <button
              type="button"
              onClick={() => setTab('events')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'none', border: '1px solid var(--border2)',
                borderRadius: 'var(--radius)', padding: '6px 14px',
                color: 'var(--text2)', fontSize: 13, cursor: 'pointer',
                marginBottom: 28,
              }}
            >
              <Icon name="arrowLeft" size={13} />
              Back to editing
            </button>

            {/* ── Invite form ── */}
            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
              Invite Collaborator
            </p>
            <form onSubmit={handleInvite} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => { setInviteEmail(e.target.value); setInviteFeedback(null); }}
                placeholder="Email address"
                required
                disabled={inviteSending}
                style={{ flex: 1, minWidth: 180 }}
              />
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                disabled={inviteSending}
                style={{ padding: '8px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13 }}
              >
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <button
                type="submit"
                className="btn-primary btn-sm"
                disabled={inviteSending || !inviteEmail.trim()}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Icon name="userPlus" size={13} />
                {inviteSending ? 'Sending…' : 'Send Invite'}
              </button>
            </form>
            {inviteFeedback && (
              <p style={{ fontSize: 12, marginBottom: 16, color: inviteFeedback.type === 'success' ? 'var(--success)' : 'var(--danger)' }}>
                {inviteFeedback.msg}
              </p>
            )}

            {collabLoading ? (
              <p style={{ color: 'var(--text3)', fontSize: 13, padding: '32px 0' }}>Loading…</p>
            ) : (
              <>
                {/* ── Active collaborators ── */}
                {collaborators.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                      Active Collaborators
                    </p>
                    {collaborators.map(c => (
                      <div
                        key={c.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          background: 'var(--surface)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 8,
                        }}
                      >
                        {c.avatar
                          ? <img src={c.avatar} alt={c.name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)33', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>{c.name[0]}</div>
                        }
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</p>
                          <p style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.email}</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          <select
                            value={c.role}
                            onChange={e => handleRoleChange(c.id, e.target.value)}
                            disabled={updatingRoleId === c.id}
                            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)', fontSize: 12 }}
                          >
                            <option value="editor">Editor</option>
                            <option value="viewer">Viewer</option>
                          </select>
                          <button
                            className="icon-btn danger"
                            onClick={() => handleRemoveCollaborator(c.id)}
                            disabled={removingUserId === c.id}
                            aria-label={`Remove ${c.name}`}
                            title="Remove collaborator"
                          >
                            <Icon name="trash" size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Pending invites ── */}
                {pendingInvites.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                      Pending Invites
                    </p>
                    {pendingInvites.map(inv => (
                      <div
                        key={inv.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          background: 'var(--surface)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 8,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, color: 'var(--text)', marginBottom: 1 }}>{inv.invitedEmail}</p>
                          <p style={{ fontSize: 11, color: 'var(--text3)' }}>
                            {inv.role} · expires {new Date(inv.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                        <span style={{ fontSize: 11, background: 'var(--accent)22', color: 'var(--accent)', borderRadius: 4, padding: '2px 8px' }}>
                          Pending
                        </span>
                        <button
                          className="icon-btn"
                          onClick={() => handleCancelInvite(inv.id)}
                          disabled={cancellingInvId === inv.id}
                          aria-label="Cancel invite"
                          title="Cancel invite"
                        >
                          <Icon name="x" size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {collaborators.length === 0 && pendingInvites.length === 0 && (
                  <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 24 }}>
                    No collaborators yet. Use the form above to invite someone.
                  </p>
                )}

                {/* ── Transfer Ownership section ── */}
                <div className="ot-section">
                  <p className="ot-section-label">Transfer Ownership</p>

                  {pendingTransfer ? (
                    /* Pending transfer banner */
                    <div className="ot-pending-banner">
                      <span className="ot-pending-icon">⏳</span>
                      <div className="ot-pending-text">
                        <strong>Transfer pending</strong>
                        <span>
                          Waiting for <strong>{pendingTransfer.toUserName}</strong> to accept.
                          Offer expires {new Date(pendingTransfer.expiresAt).toLocaleDateString()}.
                        </span>
                      </div>
                      <button
                        className="ot-cancel-link"
                        onClick={handleCancelTransfer}
                        disabled={cancellingTransfer}
                      >
                        {cancellingTransfer ? 'Cancelling…' : 'Cancel Transfer'}
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="ot-description">
                        Send an email invitation to one of your collaborators to take ownership.
                        They must accept within 24 hours. You will become an editor.
                      </p>
                      {collaborators.filter(c => c.role !== 'owner').length === 0 ? (
                        <p style={{ fontSize: 13, color: 'var(--text3)' }}>
                          Add at least one collaborator before transferring ownership.
                        </p>
                      ) : (
                        <button
                          className="ot-initiate-btn"
                          onClick={() => { setTransferToUserId(''); setTransferReason(''); setTransferConfirmed(false); setTransferError(''); setShowTransferModal(true); }}
                        >
                          Transfer Ownership…
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* ── Collaboration Activity Log ── */}
                <div className="act-section">
                  <p className="act-section-label">Activity Log</p>

                  {activityLoading && activity.length === 0 ? (
                    <p className="act-empty">Loading…</p>
                  ) : activity.length === 0 ? (
                    <p className="act-empty">No activity yet.</p>
                  ) : (
                    <ul className="act-list">
                      {activity.map(item => (
                        <li key={item.id} className="act-item">
                          <div className="act-avatar" aria-hidden="true">
                            {(item.userName || '?')[0].toUpperCase()}
                          </div>
                          <div className="act-body">
                            <span className="act-name">{item.userName}</span>
                            {' '}
                            <span className="act-desc">{describeActivity(item)}</span>
                          </div>
                          <time className="act-time" dateTime={new Date(item.createdAt).toISOString()} title={new Date(item.createdAt).toLocaleString()}>
                            {relativeTime(item.createdAt)}
                          </time>
                        </li>
                      ))}
                    </ul>
                  )}

                  {activityHasMore && (
                    <button
                      className="act-load-more"
                      onClick={handleLoadMoreActivity}
                      disabled={activityLoading}
                    >
                      {activityLoading ? 'Loading…' : 'Load more'}
                    </button>
                  )}
                </div>
              </>
            )}
            </div>{/* maxWidth wrapper */}
          </div>
        )}

        {/* Discussions tab — all collaborators */}
        {tab === 'discussions' && (
          <div
            id="editor-panel-discussions"
            role="tabpanel"
            aria-labelledby="editor-tab-discussions"
            className="editor-panel"
            style={{ padding: '28px 24px', overflowY: 'auto', flex: 1, minHeight: 0 }}
          >
            <button type="button" onClick={() => setTab('events')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '6px 14px', color: 'var(--text2)', fontSize: 13, cursor: 'pointer', marginBottom: 24 }}>
              <Icon name="arrowLeft" size={13} />Back to editing
            </button>
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.6 }}>
              Discussion is visible to all collaborators on this timeline.
            </p>

            {/* New comment composer */}
            <div className="disc-composer">
              <div className="disc-avatar-sm" aria-hidden="true">
                {(currentUser?.name || '?')[0].toUpperCase()}
              </div>
              <div className="disc-composer-right">
                <textarea
                  className="disc-textarea"
                  rows={3}
                  value={discNewBody}
                  onChange={e => { setDiscNewBody(e.target.value.slice(0, 2000)); setDiscError(''); }}
                  placeholder="Start a discussion…"
                />
                <div className="disc-composer-footer">
                  <span className="disc-char-count">{discNewBody.length}/2000</span>
                  <button
                    className="disc-post-btn"
                    onClick={handlePostDiscussionComment}
                    disabled={discPosting || !discNewBody.trim()}
                  >
                    {discPosting ? 'Posting…' : 'Post'}
                  </button>
                </div>
              </div>
            </div>

            {discError && (
              <p role="alert" className="disc-error">{discError}</p>
            )}

            {/* Thread list */}
            {discLoading && discussions.length === 0 ? (
              <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 24 }}>Loading…</p>
            ) : discussions.length === 0 ? (
              <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 24 }}>
                No discussion yet. Be the first to post!
              </p>
            ) : (
              <div className="disc-thread-list">
                {discussions.map(comment => (
                  <div key={comment.id} className="disc-thread">
                    {/* Top-level comment */}
                    <div className="disc-comment">
                      <div className="disc-avatar" aria-hidden="true">
                        {comment.isDeleted ? '×' : (comment.userName || '?')[0].toUpperCase()}
                      </div>
                      <div className="disc-comment-body">
                        {comment.isDeleted ? (
                          <p className="disc-deleted">[This comment was deleted]</p>
                        ) : (
                          <>
                            <div className="disc-meta">
                              <span className="disc-name">{comment.userName}</span>
                              <time className="disc-time" title={new Date(comment.createdAt).toLocaleString()}>
                                {relativeTime(comment.createdAt)}
                              </time>
                              {comment.editedAt && <span className="disc-edited">(edited)</span>}
                            </div>
                            <p className="disc-text">{comment.body}</p>
                            <div className="disc-actions">
                              <button
                                className="disc-reply-trigger"
                                onClick={() => {
                                  setDiscReplyingTo(discReplyingTo === comment.id ? null : comment.id);
                                  setDiscReplyBody('');
                                }}
                              >
                                {discReplyingTo === comment.id ? '↑ Cancel' : '↩ Reply'}
                              </button>
                              {(comment.userId === currentUser?.id || isOwner) && (
                                <button
                                  className="disc-action-btn disc-delete-btn"
                                  onClick={() => handleDeleteDiscussionComment(comment.id)}
                                  disabled={discDeleteId === comment.id}
                                >
                                  {discDeleteId === comment.id ? 'Deleting…' : 'Delete'}
                                </button>
                              )}
                            </div>
                            {discReplyingTo === comment.id && (
                              <div className="disc-reply-composer">
                                <div className="disc-avatar-sm" aria-hidden="true">
                                  {(currentUser?.name || '?')[0].toUpperCase()}
                                </div>
                                <div className="disc-composer-right">
                                  <textarea
                                    className="disc-textarea disc-textarea-sm"
                                    rows={2}
                                    value={discReplyBody}
                                    onChange={e => setDiscReplyBody(e.target.value.slice(0, 2000))}
                                    placeholder={`Reply to ${comment.userName}…`}
                                    autoFocus
                                  />
                                  <div className="disc-composer-footer">
                                    <span className="disc-char-count">{discReplyBody.length}/2000</span>
                                    <button
                                      className="disc-post-btn disc-post-btn-sm"
                                      onClick={() => handlePostDiscussionReply(comment.id)}
                                      disabled={discReplyPosting || !discReplyBody.trim()}
                                    >
                                      {discReplyPosting ? 'Posting…' : 'Post Reply'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {/* Replies */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="disc-replies">
                        {comment.replies.map(reply => (
                          <div key={reply.id} className="disc-reply">
                            <div className="disc-avatar disc-avatar-sm" aria-hidden="true">
                              {(reply.userName || '?')[0].toUpperCase()}
                            </div>
                            <div className="disc-reply-body">
                              <div className="disc-meta">
                                <span className="disc-name">{reply.userName}</span>
                                <time className="disc-time" title={new Date(reply.createdAt).toLocaleString()}>
                                  {relativeTime(reply.createdAt)}
                                </time>
                                {reply.editedAt && <span className="disc-edited">(edited)</span>}
                              </div>
                              <p className="disc-text">{reply.body}</p>
                              <div className="disc-actions">
                                <button
                                  className="disc-reply-trigger"
                                  onClick={() => {
                                    setDiscReplyingTo(comment.id);
                                    setDiscReplyBody(`@${reply.userName} `);
                                  }}
                                >
                                  ↩ Reply
                                </button>
                                {(reply.userId === currentUser?.id || isOwner) && (
                                  <button
                                    className="disc-action-btn disc-delete-btn"
                                    onClick={() => handleDeleteDiscussionReply(reply.id, comment.id)}
                                    disabled={discDeleteReply === reply.id}
                                  >
                                    {discDeleteReply === reply.id ? 'Deleting…' : 'Delete'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        </div>

        {/* Preview pane — on mobile, floating Save button overlays the canvas */}
        <div className="editor-preview" style={{ padding: 32, overflow: 'auto', position: 'relative', display: (tab !== 'events' && tab !== 'design') ? 'none' : undefined, ...(isMobile && mobileTab === 'preview' ? { background: (previewColors || tl.colors || DEFAULT_COLORS).tertiary } : {}) }}>
          <p style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
            Preview — {{ horizontal: 'Horizontal', vertical: 'Vertical', zigzag: 'Zigzag', storyline: 'Storyline', newspaper: 'Newspaper', swimlane: 'Swimlane', tree: 'Tree' }[tl.orientation] || 'Vertical'} Timeline
          </p>
          <TimelineCanvas
            timeline={previewColors ? { ...tl, colors: previewColors } : tl}
            onEventClick={setSelectedEvent}
            onReorderLanes={canEdit && tl.orientation === 'swimlane' ? (newOrder) => update({ swimlanes: newOrder }) : undefined}
            canEdit={canEdit}
            onEditEvent={canEdit ? openEditEvent : undefined}
            onAddEventToChapter={canEdit ? handleAddEventToChapter : undefined}
            onColorsChange={canEdit ? (patch) => {
              const updatedTl = { ...tl, colors: { ...(tl.colors || DEFAULT_COLORS), ...patch } };
              setTl(updatedTl);
              setIsDirty(true);
              if (designSaveTimerRef.current) clearTimeout(designSaveTimerRef.current);
              designSaveTimerRef.current = setTimeout(() => autoSave(updatedTl), 700);
            } : undefined}
          />
        </div>
      </div>

      {/* ── Mobile bottom tab bar ── */}
      {isMobile && (
        <nav className="mobile-tab-bar" aria-label="Editor sections">
          {[
            { id: 'events',  icon: 'feed',    label: 'Events'  },
            { id: 'design',  icon: 'palette', label: 'Design'  },
            { id: 'preview', icon: 'eye',     label: 'Preview' },
            { id: 'more',    icon: 'history', label: 'More'    },
          ].map(({ id, icon, label }) => (
            <button
              key={id}
              className={`mobile-tab-btn${mobileTab === id ? ' mobile-tab-btn--active' : ''}`}
              onClick={() => setMobileTabSynced(id)}
              aria-label={label}
              aria-pressed={mobileTab === id}
            >
              <Icon name={icon} size={22} />
              <span className="mobile-tab-label">{label}</span>
            </button>
          ))}
        </nav>
      )}

      {/* ── Add / Edit event modal ── */}
      <Modal open={showEventForm} onClose={closeEventForm} title={editingEvent?.id ? 'Edit Event' : 'Add Event'}>
        <EventForm
          initial={editingEvent}
          onSave={saveEvent}
          onCancel={closeEventForm}
          existingChapters={getChapterOrder()}
          requireLocation={tl.orientation === 'map'}
          existingSwimlanes={getSwimlaneOrder()}
          showProgress={tl.orientation === 'swimlane'}
          showTreeFields={tl.orientation === 'tree' || tl.events.some(e => e.parentEventId)}
          showSpineFields={tl.orientation === 'spine'}
          showFeatured={tl.orientation === 'zigzag'}
          showNewspaperFields={tl.orientation === 'newspaper'}
          showMapFields={tl.orientation === 'map'}
          existingEvents={tl.events}
          editingEventId={editingEvent?.id || null}
        />
      </Modal>

      {/* ── CSV Import modal ── */}
      <CSVImportModal
        open={showCSVImport}
        onClose={() => setShowCSVImport(false)}
        timelineId={tl.id}
        onImported={(result) => {
          setShowCSVImport(false);
          handleImported(result);
        }}
      />

      {/* ── GEDCOM Import modal ── */}
      <GEDCOMImportModal
        open={showGEDCOMImport}
        onClose={() => setShowGEDCOMImport(false)}
        timelineId={tl.id}
        isTreeLayout={tl.orientation === 'tree'}
        onImported={(result) => {
          setShowGEDCOMImport(false);
          handleImported(result);
        }}
      />

      {/* ── Import undo toast ── */}
      {undoImport && (
        <div className="imp-undo-toast">
          <span>
            {undoImport.imported} event{undoImport.imported !== 1 ? 's' : ''} imported.
          </span>
          <button className="imp-undo-btn" onClick={handleUndoImport}>
            Undo
          </button>
          <button className="imp-undo-close" onClick={() => { clearTimeout(undoTimerRef.current); setUndoImport(null); }} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {/* ── Version preview modal ── */}
      <Modal
        open={!!previewVersion}
        onClose={() => setPreviewVersion(null)}
        title={previewVersion ? `v${previewVersion.versionNumber} — ${previewVersion.title}` : ''}
        width={900}
      >
        {previewVersion && (
          <div>
            <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
              Read-only preview · Saved{' '}
              {new Date(previewVersion.savedAt).toLocaleString(undefined, {
                weekday: 'short', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
              {previewVersion.label && (
                <span style={{ marginLeft: 10, background: 'var(--accent)22', color: 'var(--accent)', borderRadius: 5, padding: '1px 8px', fontSize: 11 }}>
                  {previewVersion.label}
                </span>
              )}
            </p>
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 16 }}>
              <TimelineCanvas timeline={previewVersion} onEventClick={() => {}} />
            </div>
            {previewVersion.events?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Events in this version ({previewVersion.events.length})
                </p>
                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)' }}>
                  {previewVersion.events.map((ev, i) => (
                    <div key={ev.id || i} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '7px 12px', borderBottom: i < previewVersion.events.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', flexShrink: 0 }}>{ev.startDate?.slice(0, 6) ? String(parseInt(ev.startDate.slice(0, 6), 10) - 99999) : '—'}</span>
                      <span style={{ fontSize: 13, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title || '(untitled)'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button className="btn-ghost" onClick={() => setPreviewVersion(null)}>Close</button>
              {canEdit && (
                <button
                  className="btn-primary"
                  onClick={() => { setRestoreTarget(previewVersion); setPreviewVersion(null); }}
                >
                  Restore This Version
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* ── Restore confirmation modal ── */}
      <Modal
        open={!!restoreTarget}
        onClose={() => { if (!restoring) { setRestoreTarget(null); setRestorePreLabel(''); } }}
        title="Restore Version"
        width={480}
      >
        {restoreTarget && (
          <div>
            <p style={{ color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
              Restore <strong style={{ color: 'var(--text)' }}>v{restoreTarget.versionNumber} — {restoreTarget.title}</strong>?
              Your current timeline will be overwritten. A snapshot of the current state will be saved automatically so you can undo this restore if needed.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', display: 'block', marginBottom: 6 }}>
                Label for the current version before restoring (optional)
              </label>
              <input
                value={restorePreLabel}
                onChange={e => setRestorePreLabel(e.target.value.slice(0, 100))}
                placeholder='e.g. "Before restore" — leave blank to skip'
                style={{ width: '100%', boxSizing: 'border-box' }}
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter' && !restoring) confirmRestore(); }}
              />
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn-ghost"
                disabled={restoring}
                onClick={() => { setRestoreTarget(null); setRestorePreLabel(''); }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={confirmRestore}
                disabled={restoring}
                style={{ opacity: restoring ? 0.7 : 1 }}
              >
                {restoring ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Unsaved-changes warning ── */}
      <Modal open={showLeaveWarning} onClose={() => setShowLeaveWarning(false)} title="Unsaved Changes" width={400}>
        <p style={{ color: 'var(--text2)', marginBottom: 24 }}>
          You have unsaved changes. If you leave now your changes will be lost.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => setShowLeaveWarning(false)}>Keep Editing</button>
          <button className="btn-danger" onClick={() => { setShowLeaveWarning(false); onBack(); }}>Leave Without Saving</button>
        </div>
      </Modal>

      {/* ── Delete event confirmation ── */}
      <Modal open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} title="Delete Event" width={400}>
        {deleteConfirmEvent && (
          <div>
            <p style={{ color: 'var(--text2)', marginBottom: 24, lineHeight: 1.6 }}>
              Delete <strong style={{ color: 'var(--text)' }}>{deleteConfirmEvent.title}</strong>?{' '}
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => setDeleteConfirmId(null)}>Cancel</button>
              <button
                className="btn-danger"
                onClick={() => { deleteEvent(deleteConfirmId); setDeleteConfirmId(null); }}
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Keyboard shortcuts reference ── */}
      <Modal open={showShortcutsModal} onClose={() => setShowShortcutsModal(false)} title="Keyboard Shortcuts" width={480}>
        <div>
          <div className="shortcuts-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px 16px', paddingBottom: 10, marginBottom: 4, borderBottom: '1px solid var(--border)' }}>
            {['Action', 'Mac', 'Windows / Linux'].map(h => (
              <span key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</span>
            ))}
          </div>
          {[
            ['Save',            '⌘S',              'Ctrl+S'],
            ['New Event',       '⌘E',              'Ctrl+E'],
            ['Toggle Layout',   '⌘L',              'Ctrl+L'],
            ['Duplicate Event', '⌘D',              'Ctrl+D'],
            ['Delete Event',    '⌫ / Delete',      'Backspace / Delete'],
            ['Navigate Events', '↑  ↓',            '↑  ↓'],
            ['Close Modal',     'Esc',              'Esc'],
            ['Show Shortcuts',  '?',               '?'],
          ].map(([action, mac, win]) => (
            <div key={action} className="shortcuts-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px 16px', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 13, color: 'var(--text)' }}>{action}</span>
              <kbd style={{ fontSize: 11, background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 4, padding: '2px 8px', fontFamily: 'monospace', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{mac}</kbd>
              <kbd style={{ fontSize: 11, background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 4, padding: '2px 8px', fontFamily: 'monospace', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{win}</kbd>
            </div>
          ))}
          <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 14, lineHeight: 1.6 }}>
            Duplicate, Delete, and Navigate shortcuts activate when an event card is focused in the sidebar.
          </p>
        </div>
      </Modal>

      {/* ── Event detail popup ── */}
      <Modal open={!!selectedEvent} onClose={() => setSelectedEvent(null)} title={selectedEvent?.title || ''} width={520}>
        {selectedEvent && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, background: 'var(--bg3)', borderRadius: 6, padding: '4px 10px', color: 'var(--accent)' }}>
                {formatDate(selectedEvent.startDate, selectedEvent.startEra !== 'NONE')}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text3)', alignSelf: 'center' }} aria-hidden="true">→</span>
              <span style={{ fontSize: 12, background: 'var(--bg3)', borderRadius: 6, padding: '4px 10px', color: 'var(--accent)' }}>
                {formatDate(selectedEvent.endDate, selectedEvent.endEra !== 'NONE')}
              </span>
            </div>

            {selectedEvent.photo && (
              <img
                src={selectedEvent.photo}
                alt={`Photo for ${selectedEvent.title}`}
                style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'contain', background: 'var(--bg2)', borderRadius: 'var(--radius)', marginBottom: 16, border: '1px solid var(--border)' }}
              />
            )}

            {selectedEvent.videoUrl && (() => {
              const ytSrc = (() => {
                try {
                  const u = new URL(selectedEvent.videoUrl.trim());
                  const h = u.hostname.replace('www.', '').replace('m.', '');
                  let id = null;
                  if (h === 'youtu.be') id = u.pathname.slice(1).split('/')[0];
                  else if (h === 'youtube.com' || h === 'youtube-nocookie.com') {
                    id = (u.pathname.startsWith('/embed/') || u.pathname.startsWith('/shorts/'))
                      ? u.pathname.split('/')[2]
                      : u.searchParams.get('v');
                  }
                  return id && /^[a-zA-Z0-9_-]{11}$/.test(id)
                    ? `https://www.youtube-nocookie.com/embed/${id}`
                    : null;
                } catch { return null; }
              })();
              return ytSrc ? (
                <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: 8, overflow: 'hidden', marginBottom: 16, background: 'var(--bg2)' }}>
                  <iframe
                    src={ytSrc}
                    title={`Video for ${selectedEvent.title}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                  />
                </div>
              ) : null;
            })()}

            {hasVisibleContent(selectedEvent.description) ? (
              <div
                dangerouslySetInnerHTML={{ __html: selectedEvent.description }}
                style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.7, marginBottom: selectedEvent.sources?.length ? 20 : 0 }}
              />
            ) : (
              <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.7, marginBottom: selectedEvent.sources?.length ? 20 : 0 }}>
                No description provided.
              </p>
            )}

            {selectedEvent.sources?.length > 0 && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                  Sources ({selectedEvent.sources.length})
                </p>
                {selectedEvent.sources.map((src, i) => (
                  <div
                    key={src.id || i}
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 8 }}
                  >
                    {src.label && <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', marginBottom: src.text ? 6 : 0 }}>{src.label}</p>}
                    {src.text  && <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>{src.text}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Transfer Ownership modal ── */}
      <Modal
        open={showTransferModal}
        onClose={() => { if (!initiatingTransfer) { setShowTransferModal(false); setTransferError(''); } }}
        title="Transfer Ownership"
        width={500}
      >
        <div>
          {/* Warning banner */}
          <div className="ot-modal-warning">
            <strong>This will send an email to the recipient.</strong> They must click Accept within 24 hours.
            You will become an editor and lose admin rights over this timeline.
          </div>

          {/* Recipient selector */}
          <div style={{ marginBottom: 16 }}>
            <label className="ot-field-label">Transfer to</label>
            <select
              className="ot-select"
              value={transferToUserId}
              onChange={e => { setTransferToUserId(e.target.value); setTransferError(''); }}
              autoFocus
            >
              <option value="">— select a collaborator —</option>
              {collaborators.filter(c => c.role !== 'owner').map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.role})
                </option>
              ))}
            </select>
          </div>

          {/* Optional reason */}
          <div style={{ marginBottom: 16 }}>
            <label className="ot-field-label">Message to recipient <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(optional, max 500 chars)</span></label>
            <textarea
              className="ot-textarea"
              value={transferReason}
              onChange={e => setTransferReason(e.target.value.slice(0, 500))}
              placeholder="Why are you transferring ownership?"
              rows={3}
            />
            <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, textAlign: 'right' }}>
              {transferReason.length}/500
            </p>
          </div>

          {/* Confirmation checkbox */}
          <label className="ot-confirm-label">
            <input
              type="checkbox"
              checked={transferConfirmed}
              onChange={e => setTransferConfirmed(e.target.checked)}
            />
            I understand I will become an editor if the recipient accepts.
          </label>

          {transferError && (
            <p role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>{transferError}</p>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
            <button
              className="btn-ghost"
              disabled={initiatingTransfer}
              onClick={() => { setShowTransferModal(false); setTransferError(''); }}
            >
              Cancel
            </button>
            <button
              className="ot-send-btn"
              onClick={handleInitiateTransfer}
              disabled={initiatingTransfer || !transferToUserId || !transferConfirmed}
            >
              {initiatingTransfer ? 'Sending…' : 'Send Transfer Offer'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TimelineEditor;
